/* global CustomEvent, CSS, FormData, IntersectionObserver, MutationObserver, ResizeObserver, URL, console, document, navigator, queueMicrotask, window */

const VARIANT_CONFIRM_TIMEOUT_MS = 12000

function toId(value) {
  if (value === null || value === undefined || value === '') return null
  return String(value)
}

export function readNamedFormControlValue(form, name) {
  const control = form?.elements?.namedItem?.(name)
  return control && 'value' in control ? control.value : null
}

function nonNegativeNumber(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return 0
  const source = typeof value === 'string' ? value.trim() : value
  if (typeof source === 'string' && !/^(?:\d+(?:\.\d+)?|\.\d+)(?:px)?$/u.test(source)) {
    return 0
  }
  const parsed =
    typeof source === 'string' && source.endsWith('px')
      ? Number(source.slice(0, -2))
      : Number(source)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
}

export function captureViewportScroll(windowRef = typeof window === 'undefined' ? null : window) {
  return {
    left: nonNegativeNumber(windowRef?.scrollX),
    top: nonNegativeNumber(windowRef?.scrollY),
  }
}

export function restoreViewportScroll(
  windowRef = typeof window === 'undefined' ? null : window,
  position = {},
) {
  if (typeof windowRef?.scrollTo !== 'function') return false

  windowRef.scrollTo({
    behavior: 'auto',
    left: nonNegativeNumber(position.left),
    top: nonNegativeNumber(position.top),
  })
  return true
}

export function capturePackageVariantViewport({
  documentRef = typeof document === 'undefined' ? null : document,
  input = null,
  packageOptionPosition,
  releaseFocus = true,
  root,
  windowRef = typeof window === 'undefined' ? null : window,
} = {}) {
  const activeElement = input ?? documentRef?.activeElement
  const optionPosition = toId(packageOptionPosition)
  if (
    !activeElement ||
    !optionPosition ||
    !root?.contains?.(activeElement) ||
    toId(activeElement.getAttribute?.('data-option-position')) !== optionPosition
  ) {
    return null
  }

  const token = {
    focusId: activeElement.id || '',
    optionPosition,
    scroll: captureViewportScroll(windowRef),
    value: activeElement.getAttribute?.('value') ?? activeElement.value ?? '',
  }
  if (releaseFocus) activeElement.blur?.()
  return token
}

export function resolvePackageVariantInteractionInput({
  documentRef = typeof document === 'undefined' ? null : document,
  packageOptionPosition,
  root,
  target,
} = {}) {
  const optionPosition = toId(packageOptionPosition)
  if (!optionPosition || !target) return null

  let input = target.matches?.('input[data-option-position]') ? target : null
  if (!input) {
    const label = target.closest?.('label[for]')
    const inputId = label?.getAttribute?.('for')
    input = inputId ? documentRef?.getElementById?.(inputId) : null
  }

  if (
    !input ||
    !root?.contains?.(input) ||
    toId(input.getAttribute?.('data-option-position')) !== optionPosition
  ) {
    return null
  }
  return input
}

export function capturePackageVariantPointerViewport({
  documentRef = typeof document === 'undefined' ? null : document,
  packageOptionPosition,
  root,
  target,
  windowRef = typeof window === 'undefined' ? null : window,
} = {}) {
  const input = resolvePackageVariantInteractionInput({
    documentRef,
    packageOptionPosition,
    root,
    target,
  })
  if (!input) return null

  const token = capturePackageVariantViewport({
    documentRef,
    input,
    packageOptionPosition,
    releaseFocus: false,
    root,
    windowRef,
  })
  if (!token) return null

  input.focus?.({ preventScroll: true })
  restoreViewportScroll(windowRef, token.scroll)
  return token
}

export function restorePackageVariantViewport({
  documentRef = typeof document === 'undefined' ? null : document,
  root,
  token,
  windowRef = typeof window === 'undefined' ? null : window,
} = {}) {
  if (!root || !token) return false

  const idMatch = token.focusId ? documentRef?.getElementById?.(token.focusId) : null
  const focusTarget =
    (idMatch && root.contains?.(idMatch) ? idMatch : null) ??
    [...(root.querySelectorAll?.('input[data-option-position]') ?? [])].find(
      (candidate) =>
        toId(candidate.getAttribute?.('data-option-position')) === toId(token.optionPosition) &&
        String(candidate.getAttribute?.('value') ?? candidate.value ?? '') === String(token.value),
    )

  focusTarget?.focus?.({ preventScroll: true })
  return restoreViewportScroll(windowRef, token.scroll)
}

function enabledImpactOwner(value) {
  return value === true || value === 1 || (typeof value === 'string' && value.trim() === '1')
}

function normalizedHeaderVisibility(value) {
  if (value === null || value === undefined) return 1
  if (typeof value !== 'number' && typeof value !== 'string') return 0
  const source = typeof value === 'string' ? value.trim() : value
  if (source === '') return 1
  if (typeof source === 'string' && !/^(?:\d+(?:\.\d+)?|\.\d+)$/u.test(source)) {
    return 0
  }
  const parsed = Number(source)
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 0
}

export function resolveMediaStickyObstructionState({
  fixedBarVisible = false,
  fixedBarHeight = 0,
  stickyHeaderEnabled = 0,
  headerHeight = 0,
  headerVisibility,
  stickyAnnouncementEnabled = 0,
  announcementHeight = 0,
} = {}) {
  const fixedBarObstructionHeight = fixedBarVisible === true ? nonNegativeNumber(fixedBarHeight) : 0
  const headerObstructionHeight = enabledImpactOwner(stickyHeaderEnabled)
    ? nonNegativeNumber(headerHeight) * normalizedHeaderVisibility(headerVisibility)
    : 0
  const announcementObstructionHeight = enabledImpactOwner(stickyAnnouncementEnabled)
    ? nonNegativeNumber(announcementHeight)
    : 0

  return {
    announcementObstructionHeight,
    bottomObstructionHeight: fixedBarObstructionHeight,
    fixedBarObstructionHeight,
    headerObstructionHeight,
    topObstructionHeight: headerObstructionHeight + announcementObstructionHeight,
  }
}

export function resolveMediaStickyTop({
  viewportHeight,
  railHeight,
  defaultTop,
  bottomGap = 16,
  obstructionHeight = 0,
} = {}) {
  const viewport = nonNegativeNumber(viewportHeight)
  const rail = nonNegativeNumber(railHeight)
  const preferredTop = nonNegativeNumber(defaultTop)
  const gap = nonNegativeNumber(bottomGap)
  const obstruction = nonNegativeNumber(obstructionHeight)
  const bottomAnchoredTop = viewport - rail - gap - obstruction

  return preferredTop + rail + gap + obstruction > viewport ? bottomAnchoredTop : preferredTop
}

export function resolveViewportBottomOverlap({
  viewportHeight,
  obstructionTop,
  obstructionBottom,
} = {}) {
  const viewport = nonNegativeNumber(viewportHeight)
  const top = nonNegativeNumber(obstructionTop)
  const bottom = nonNegativeNumber(obstructionBottom)
  if (viewport === 0 || bottom <= top || top >= viewport || bottom <= 0) return 0
  return Math.max(0, Math.min(viewport, bottom) - Math.max(0, top))
}

export function applyFixedBarViewportBottomOffset(fixedBar, obstructionHeight = 0) {
  if (!fixedBar?.style) return 0

  const offset = nonNegativeNumber(obstructionHeight)
  if (offset === 0) {
    fixedBar.style.removeProperty('--mpf-fixed-bar-bottom')
    return 0
  }

  const roundedOffset = Math.round(offset)
  fixedBar.style.setProperty('--mpf-fixed-bar-bottom', `${roundedOffset}px`)
  return roundedOffset
}

export function applyFixedBarVisibilityState(fixedBar, visible) {
  if (!fixedBar) return false

  const nextVisible = visible === true
  const changed = fixedBar.hasAttribute('data-visible') !== nextVisible
  fixedBar.toggleAttribute('data-visible', nextVisible)
  fixedBar.toggleAttribute('inert', !nextVisible)
  fixedBar.setAttribute('aria-hidden', String(!nextVisible))
  return changed
}

export function resolveFixedBarVisibilityState({
  footerVisible = false,
  mainActionsFullyUsable = false,
} = {}) {
  return footerVisible !== true && mainActionsFullyUsable !== true
}

export function createFixedBarVisibilityObserver({
  fixedBar,
  footer = null,
  mainActions,
  IntersectionObserverImpl = globalThis.IntersectionObserver,
  onVisibilityChange = () => {},
  ResizeObserverImpl = globalThis.ResizeObserver,
} = {}) {
  applyFixedBarVisibilityState(fixedBar, false)
  if (
    !fixedBar ||
    !mainActions ||
    typeof IntersectionObserverImpl !== 'function' ||
    typeof ResizeObserverImpl !== 'function'
  ) {
    return null
  }

  let disposed = false
  let fixedBarHeight = -1
  let footerObserver = null
  let footerReady = !footer
  let footerVisible = false
  let mainActionsReady = false
  let mainActionsFullyUsable = false
  let mainObserver = null
  let resizeObserver = null

  const sync = () => {
    if (disposed) return false
    const visible =
      mainActionsReady &&
      footerReady &&
      resolveFixedBarVisibilityState({ footerVisible, mainActionsFullyUsable })
    const changed = applyFixedBarVisibilityState(fixedBar, visible)
    if (changed && typeof onVisibilityChange === 'function') onVisibilityChange(visible)
    return changed
  }
  const readFixedBarHeight = () =>
    Math.ceil(nonNegativeNumber(fixedBar.getBoundingClientRect?.().height))
  const bindMainObserver = () => {
    const nextHeight = readFixedBarHeight()
    if (mainObserver && nextHeight === fixedBarHeight) return

    mainObserver?.disconnect()
    fixedBarHeight = nextHeight
    mainActionsReady = false
    mainActionsFullyUsable = false
    sync()

    const observer = new IntersectionObserverImpl(
      (entries) => {
        if (disposed || mainObserver !== observer) return
        const entry = entries.find((candidate) => candidate.target === mainActions)
        if (!entry) return
        mainActionsReady = true
        mainActionsFullyUsable = entry.isIntersecting === true && entry.intersectionRatio >= 1
        sync()
      },
      {
        rootMargin: `0px 0px -${fixedBarHeight}px 0px`,
        threshold: [0, 1],
      },
    )
    mainObserver = observer
    observer.observe(mainActions)
  }

  sync()
  bindMainObserver()

  if (footer) {
    footerObserver = new IntersectionObserverImpl(
      (entries) => {
        if (disposed) return
        const entry = entries.find((candidate) => candidate.target === footer)
        if (!entry) return
        footerReady = true
        footerVisible = entry.isIntersecting === true
        sync()
      },
      { threshold: 0 },
    )
    footerObserver.observe(footer)
  }

  resizeObserver = new ResizeObserverImpl((entries) => {
    if (disposed || !entries.some((entry) => entry.target === fixedBar)) return
    bindMainObserver()
  })
  resizeObserver.observe(fixedBar)

  return {
    disconnect() {
      if (disposed) return
      disposed = true
      mainObserver?.disconnect()
      footerObserver?.disconnect()
      resizeObserver?.disconnect()
      mainObserver = null
      footerObserver = null
      resizeObserver = null
      applyFixedBarVisibilityState(fixedBar, false)
    },
  }
}

export function applyMediaRailLifecycleTeardown({ fixedBar, infoRail, mediaRail } = {}) {
  mediaRail?.style?.removeProperty('--mpf-media-sticky-top')
  infoRail?.style?.removeProperty('--mpf-info-sticky-top')
  fixedBar?.style?.removeProperty('--mpf-fixed-bar-bottom')
  applyFixedBarVisibilityState(fixedBar, false)
}

export function createMediaRailStickyScheduler({
  windowRef = typeof window === 'undefined' ? null : window,
  documentRef = typeof document === 'undefined' ? null : document,
  MutationObserverImpl = globalThis.MutationObserver,
  ResizeObserverImpl = globalThis.ResizeObserver,
  requestAnimationFrameImpl = windowRef?.requestAnimationFrame?.bind(windowRef),
  cancelAnimationFrameImpl = windowRef?.cancelAnimationFrame?.bind(windowRef),
  getResizeTargets = () => [],
  syncResponsivePresentation = () => {},
  syncStickyGeometry = () => {},
} = {}) {
  let disposed = false
  let frameId = null
  let mutationObserver = null
  let responsivePresentationDirty = true
  let resizeObserver = null
  let resizeTargetsDirty = true

  const run = () => {
    frameId = null
    if (disposed) return
    if (resizeTargetsDirty) {
      resizeTargetsDirty = false
      refreshResizeTargets()
    }
    if (responsivePresentationDirty) {
      responsivePresentationDirty = false
      syncResponsivePresentation()
    }
    syncStickyGeometry()
  }
  const schedule = () => {
    if (disposed || frameId !== null || typeof requestAnimationFrameImpl !== 'function') return
    frameId = requestAnimationFrameImpl(run)
  }
  const onResponsiveResize = () => {
    responsivePresentationDirty = true
    schedule()
  }
  const refreshResizeTargets = () => {
    if (resizeObserver) resizeObserver.disconnect()
    resizeObserver = null
    if (disposed || typeof ResizeObserverImpl !== 'function') return

    resizeObserver = new ResizeObserverImpl(onResponsiveResize)
    for (const target of new Set(getResizeTargets().filter(Boolean))) {
      resizeObserver.observe(target)
    }
  }
  const onViewportChange = () => schedule()
  const onEditorMutation = () => {
    resizeTargetsDirty = true
    responsivePresentationDirty = true
    schedule()
  }
  const editorEvents = ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']

  if (typeof MutationObserverImpl === 'function' && documentRef?.documentElement) {
    mutationObserver = new MutationObserverImpl(schedule)
    mutationObserver.observe(documentRef.documentElement, {
      attributeFilter: ['style'],
      attributes: true,
    })
  }
  windowRef?.addEventListener?.('scroll', onViewportChange, { passive: true })
  windowRef?.addEventListener?.('resize', onResponsiveResize, { passive: true })
  for (const eventName of editorEvents) {
    documentRef?.addEventListener?.(eventName, onEditorMutation)
  }
  schedule()

  return {
    dispose() {
      if (disposed) return
      disposed = true
      windowRef?.removeEventListener?.('scroll', onViewportChange)
      windowRef?.removeEventListener?.('resize', onResponsiveResize)
      for (const eventName of editorEvents) {
        documentRef?.removeEventListener?.(eventName, onEditorMutation)
      }
      if (mutationObserver) mutationObserver.disconnect()
      mutationObserver = null
      if (resizeObserver) resizeObserver.disconnect()
      resizeObserver = null
      if (frameId !== null && typeof cancelAnimationFrameImpl === 'function') {
        cancelAnimationFrameImpl(frameId)
      }
      frameId = null
    },
    refreshResizeTargets() {
      resizeTargetsDirty = true
      responsivePresentationDirty = true
      schedule()
    },
    schedule,
  }
}

function positiveInteger(value, fallback = 1, maximum = Number.MAX_SAFE_INTEGER) {
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed < 1) return fallback
  return Math.min(parsed, maximum)
}

function nullableNonNegativeInteger(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed)) return null
  return Math.max(parsed, 0)
}

function cloneProperties(properties) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {}
  return Object.fromEntries(
    Object.entries(properties).filter(
      ([key, value]) => key !== '' && value !== null && value !== undefined,
    ),
  )
}

function cloneSnapshotValue(value) {
  if (Array.isArray(value)) return value.map((item) => cloneSnapshotValue(item))
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, cloneSnapshotValue(item)]),
  )
}

function freezeSnapshot(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value
  for (const item of Object.values(value)) freezeSnapshot(item)
  return Object.freeze(value)
}

function freezeSelectionGraph(graph) {
  return freezeSnapshot(graph)
}

function normalizeVariant(variant) {
  const id = toId(variant?.id)
  if (!id) return null

  const snapshot = cloneSnapshotValue(variant)
  return {
    ...snapshot,
    id,
    available: variant.available === true,
  }
}

export function resolveVariantQuantityRule(variant) {
  const min = positiveInteger(variant?.quantityRule?.min)
  const increment = positiveInteger(variant?.quantityRule?.increment)
  const caps = []
  const shopifyMaximum = nullableNonNegativeInteger(variant?.quantityRule?.max)
  if (shopifyMaximum !== null) caps.push(shopifyMaximum)

  const inventoryTracked = ![null, undefined, '', false].includes(variant?.inventoryManagement)
  const inventoryPolicy = String(variant?.inventoryPolicy ?? '').toLowerCase()
  if (inventoryTracked && inventoryPolicy === 'deny') {
    caps.push(nullableNonNegativeInteger(variant?.inventoryQuantity) ?? 0)
  }

  const rawMaximum = caps.length > 0 ? Math.min(...caps) : null
  const max =
    rawMaximum === null || rawMaximum < min
      ? rawMaximum
      : min + Math.floor((rawMaximum - min) / increment) * increment
  const purchasable = variant?.available === true && (max === null || max >= min)

  return { min, increment, max, purchasable }
}

export function resolveVariantQuantity(variant, requestedQuantity) {
  const rule = resolveVariantQuantityRule(variant)
  const requested = positiveInteger(requestedQuantity, rule.min)
  const capped =
    rule.max === null
      ? Math.max(requested, rule.min)
      : Math.min(Math.max(requested, rule.min), Math.max(rule.max, rule.min))
  return rule.min + Math.floor((capped - rule.min) / rule.increment) * rule.increment
}

export function resolveMainVariant(variants, requestedId) {
  const normalized = (variants ?? []).map(normalizeVariant).filter(Boolean)
  const requested = toId(requestedId)
  const exact = normalized.find((variant) => variant.id === requested)
  if (exact) return exact
  return normalized.find((variant) => variant.available) ?? null
}

export function readSelectedOptionValueIds(form, candidateInput = null) {
  const selectedByPosition = new Map()
  const controls =
    form?.elements && typeof form.elements[Symbol.iterator] === 'function' ? [...form.elements] : []

  for (const control of controls) {
    const position = Number(control?.getAttribute?.('data-option-position'))
    if (!Number.isInteger(position) || position < 1 || control.checked !== true) continue
    selectedByPosition.set(position, toId(control.value))
  }

  const candidatePosition = Number(candidateInput?.getAttribute?.('data-option-position'))
  if (Number.isInteger(candidatePosition) && candidatePosition > 0) {
    selectedByPosition.set(candidatePosition, toId(candidateInput.value))
  }
  if (selectedByPosition.size === 0) return []

  const maximumPosition = Math.max(...selectedByPosition.keys())
  return Array.from(
    { length: maximumPosition },
    (_, index) => selectedByPosition.get(index + 1) ?? null,
  )
}

export function resolveMainVariantForOption(variants, optionValue, selectedOptionValueIds = []) {
  const requested = toId(optionValue)
  if (!requested) return null
  const matches = (variants ?? []).filter((variant) => toId(variant?.optionValueId) === requested)
  const selection = Array.isArray(selectedOptionValueIds) ? selectedOptionValueIds.map(toId) : []
  const completeSelection = selection.length > 0 && selection.every(Boolean)
  const hasCombinationData = matches.some((variant) => Array.isArray(variant?.optionValueIds))

  if (completeSelection && hasCombinationData) {
    return (
      matches.find((variant) => {
        const variantSelection = variant.optionValueIds.map(toId)
        return (
          variantSelection.length === selection.length &&
          selection.every((value, index) => variantSelection[index] === value)
        )
      }) ?? null
    )
  }

  return matches.length === 1 ? matches[0] : null
}

export function resolveAddonVariant(product, requestedId) {
  const variants = (product?.variants ?? []).map(normalizeVariant).filter(Boolean)
  const requested = toId(requestedId)
  const exact = variants.find(
    (variant) => variant.id === requested && resolveVariantQuantityRule(variant).purchasable,
  )
  if (exact) return exact
  return variants.find((variant) => resolveVariantQuantityRule(variant).purchasable) ?? null
}

export function shouldShowAddonVariantSelector(product) {
  return (product?.variants ?? []).map(normalizeVariant).filter(Boolean).length >= 2
}

export function normalizeCareProduct(product) {
  const id = toId(product?.id)
  if (
    !id ||
    product.deleted === true ||
    product.published === false ||
    product.storefrontVisible === false
  ) {
    return null
  }

  const variants = (product.variants ?? []).map(normalizeVariant).filter(Boolean)
  if (variants.length === 0) return null
  const resolvedVariant = resolveAddonVariant({ variants }, product.selectedVariantId)
  if (!resolvedVariant) return null

  return {
    ...product,
    id,
    variants,
    kind: 'care',
    resolvedVariantId: resolvedVariant.id,
  }
}

export function normalizeAddonProducts(products) {
  const validCareById = new Map()
  for (const product of products ?? []) {
    if (product?.kind !== 'care') continue
    const careProduct = normalizeCareProduct(product)
    if (careProduct) validCareById.set(careProduct.id, careProduct)
  }

  const seen = new Set()
  const normalized = []

  for (const product of products ?? []) {
    const id = toId(product?.id)
    if (
      !id ||
      seen.has(id) ||
      product.deleted === true ||
      product.published === false ||
      product.storefrontVisible === false
    ) {
      continue
    }

    const preferredCareProduct = validCareById.get(id)
    if (preferredCareProduct) {
      normalized.push(preferredCareProduct)
      seen.add(id)
      continue
    }

    if (product.kind === 'care') {
      const careProduct = normalizeCareProduct(product)
      if (!careProduct) continue
      normalized.push(careProduct)
      seen.add(id)
      continue
    }

    const variants = (product.variants ?? []).map(normalizeVariant).filter(Boolean)
    if (variants.length === 0) continue

    const resolvedVariant = resolveAddonVariant({ variants }, product.selectedVariantId)
    normalized.push({
      ...product,
      id,
      kind: 'fbt',
      variants,
      available: resolvedVariant !== null,
      resolvedVariantId: resolvedVariant?.id ?? null,
    })
    seen.add(id)
  }

  return normalized
}

function addonStateFromProduct(product, defaultSelected) {
  const resolvedVariant = resolveAddonVariant(product, product.resolvedVariantId)
  const available = resolvedVariant !== null
  const productKind = product.kind === 'care' ? 'care' : 'fbt'
  const selectedByDefault =
    typeof product.defaultSelected === 'boolean' ? product.defaultSelected : defaultSelected

  return {
    productId: product.id,
    productKind,
    kind: productKind,
    title: product.title ?? '',
    subtitle: product.subtitle ?? '',
    featuredImage: product.featuredImage ?? '',
    url: product.url ?? '',
    visible: true,
    selected: Boolean(selectedByDefault && available),
    resolvedVariantId: resolvedVariant?.id ?? null,
    available,
    quantity: resolveVariantQuantity(resolvedVariant, undefined),
    variants: product.variants,
  }
}

export function createSelectionGraph({
  mainVariant,
  mainQuantity = 1,
  sellingPlanId = null,
  lineItemProperties = {},
  hasUnsupportedLineItemProperty = false,
  addons = [],
  defaultSelected = false,
} = {}) {
  const normalizedMain = normalizeVariant(mainVariant)
  const normalizedAddons = normalizeAddonProducts(addons)
  const addonState = Object.fromEntries(
    normalizedAddons.map((product) => [
      product.id,
      addonStateFromProduct(product, defaultSelected),
    ]),
  )

  return freezeSelectionGraph({
    mainVariantId: normalizedMain?.id ?? null,
    mainVariantAvailable: normalizedMain?.available === true,
    packageValue: normalizedMain?.title ?? '',
    mainQuantity: positiveInteger(mainQuantity),
    visibleAddonProductIds: normalizedAddons.map((product) => product.id),
    addonState,
    sellingPlanId: toId(sellingPlanId),
    lineItemProperties: cloneProperties(lineItemProperties),
    hasUnsupportedLineItemProperty: hasUnsupportedLineItemProperty === true,
    revision: 0,
  })
}

export function reconcileSelectionGraph(
  current,
  { mainVariant, addons = [], defaultSelected = false } = {},
) {
  const next = createSelectionGraph({
    mainVariant,
    mainQuantity: current?.mainQuantity,
    sellingPlanId: current?.sellingPlanId,
    lineItemProperties: current?.lineItemProperties,
    hasUnsupportedLineItemProperty: current?.hasUnsupportedLineItemProperty,
    addons,
    defaultSelected,
  })

  const addonState = Object.fromEntries(
    next.visibleAddonProductIds.map((productId) => {
      const previous = current?.addonState?.[productId]
      const candidate = next.addonState[productId]
      if (!previous) return [productId, candidate]

      const resolvedVariant = resolveAddonVariant(
        { variants: candidate.variants },
        previous.resolvedVariantId,
      )
      const available = resolvedVariant !== null
      return [
        productId,
        {
          ...candidate,
          resolvedVariantId: resolvedVariant?.id ?? null,
          available,
          kind: candidate.productKind,
          selected: Boolean(previous.selected && available),
          quantity: resolveVariantQuantity(resolvedVariant, previous.quantity),
        },
      ]
    }),
  )

  return freezeSelectionGraph({
    ...next,
    addonState,
    revision: positiveInteger(current?.revision, 0) + 1,
  })
}

export function setAddonSelection(graph, productId, selected) {
  const id = toId(productId)
  const current = id ? graph?.addonState?.[id] : null
  if (!current) return graph

  return freezeSelectionGraph({
    ...graph,
    addonState: {
      ...graph.addonState,
      [id]: {
        ...current,
        selected: Boolean(selected && current.available && current.resolvedVariantId),
      },
    },
    revision: graph.revision + 1,
  })
}

export function setMainQuantity(graph, quantity) {
  return freezeSelectionGraph({
    ...graph,
    mainQuantity: positiveInteger(quantity),
    revision: (graph?.revision ?? 0) + 1,
  })
}

export function setAddonVariant(graph, productId, variantId) {
  const id = toId(productId)
  const current = id ? graph?.addonState?.[id] : null
  if (!current) return graph

  const resolvedVariant = resolveAddonVariant({ variants: current.variants }, toId(variantId))

  return freezeSelectionGraph({
    ...graph,
    addonState: {
      ...graph.addonState,
      [id]: {
        ...current,
        resolvedVariantId: resolvedVariant?.id ?? null,
        available: resolvedVariant !== null,
        kind: current.productKind,
        selected: Boolean(current.selected && resolvedVariant),
        quantity: resolveVariantQuantity(resolvedVariant, current.quantity),
      },
    },
    revision: graph.revision + 1,
  })
}

export function setAddonQuantity(graph, productId, quantity) {
  const id = toId(productId)
  const current = id ? graph?.addonState?.[id] : null
  if (!current) return graph
  const resolvedVariant = resolveAddonVariant(
    { variants: current.variants },
    current.resolvedVariantId,
  )

  return freezeSelectionGraph({
    ...graph,
    addonState: {
      ...graph.addonState,
      [id]: {
        ...current,
        quantity: resolveVariantQuantity(resolvedVariant, quantity),
      },
    },
    revision: graph.revision + 1,
  })
}

function selectedAddonStates(graph) {
  return (graph?.visibleAddonProductIds ?? [])
    .map((productId) => graph.addonState?.[productId])
    .filter(
      (state) =>
        state?.visible && state.selected && state.available && toId(state.resolvedVariantId),
    )
}

export function resolveQuantityUnitPrice(variant, quantity) {
  const basePrice = Number(variant?.price ?? 0)
  const requestedQuantity = positiveInteger(quantity)
  let resolvedPrice = Number.isFinite(basePrice) && basePrice >= 0 ? basePrice : 0
  let resolvedMinimum = 0

  for (const priceBreak of variant?.quantityPriceBreaks ?? []) {
    const minimumQuantity = positiveInteger(priceBreak?.minimumQuantity, 0)
    const price = Number(priceBreak?.price)
    if (
      minimumQuantity > requestedQuantity ||
      minimumQuantity < resolvedMinimum ||
      !Number.isFinite(price) ||
      price < 0
    ) {
      continue
    }
    resolvedMinimum = minimumQuantity
    resolvedPrice = price
  }

  return resolvedPrice
}

export function computeSelectionTotals(graph, mainVariant) {
  const mainBasePrice = Number(mainVariant?.price ?? 0)
  const mainCompareAtPrice = Number(mainVariant?.compareAtPrice ?? 0)
  const mainQuantity = positiveInteger(graph?.mainQuantity)
  const mainPrice = resolveQuantityUnitPrice(mainVariant, mainQuantity)
  let price = mainPrice * mainQuantity
  let compareAtPrice = Math.max(mainCompareAtPrice, mainBasePrice, mainPrice) * mainQuantity

  for (const state of selectedAddonStates(graph)) {
    const variant = selectAddonDisplayVariant(state)
    const variantBasePrice = Number(variant?.price ?? 0)
    const variantCompareAtPrice = Number(variant?.compareAtPrice ?? 0)
    const quantity = positiveInteger(state.quantity)
    const variantPrice = resolveQuantityUnitPrice(variant, quantity)
    price += variantPrice * quantity
    compareAtPrice += Math.max(variantCompareAtPrice, variantBasePrice, variantPrice) * quantity
  }

  return {
    price,
    compareAtPrice,
    hasDiscount: compareAtPrice > price,
  }
}

export function shouldInterceptBundledSubmit(graph, intent) {
  if (intent === 'shop-now') return true
  if (intent !== 'add') return false
  return selectedAddonStates(graph).length > 0
}

export const GALLERY_GROUPS = Object.freeze(['features', 'videos'])

export function galleryGroupForMedia({ mediaType } = {}) {
  if (['video', 'external_video'].includes(String(mediaType ?? ''))) return 'videos'
  return 'features'
}

export function resolveGalleryFilteredPositions({
  activeGroup,
  groupedMedia = [],
  impactHiddenPositions = [],
} = {}) {
  const filtered = new Set(
    [...(impactHiddenPositions ?? [])]
      .map((position) => Number.parseInt(position, 10))
      .filter((position) => Number.isFinite(position) && position > 0),
  )

  for (const media of groupedMedia ?? []) {
    const position = Number.parseInt(media?.position, 10)
    if (media?.group !== activeGroup && Number.isFinite(position) && position > 0) {
      filtered.add(position)
    }
  }

  return filtered
}

export function resolveGalleryEnhancementState(mediaItems, nativeFilteredPositions = []) {
  const filtered = new Set(
    (nativeFilteredPositions ?? [])
      .map((position) => Number.parseInt(position, 10))
      .filter((position) => Number.isFinite(position) && position > 0),
  )
  const visibleMedia = (mediaItems ?? []).filter((_, index) => !filtered.has(index + 1))
  const availableGroups = GALLERY_GROUPS.filter((group) =>
    visibleMedia.some((media) => galleryGroupForMedia(media) === group),
  )
  const visibleMediaCount = visibleMedia.length

  return {
    availableGroups,
    visibleMediaCount,
    showTabs: visibleMediaCount > 1 && availableGroups.length > 1,
    showNavigation: visibleMediaCount > 1,
    showThumbnailControls: visibleMediaCount > 1,
  }
}

export function parseGalleryFilteredPositions(serialized, mediaCount) {
  if (typeof serialized !== 'string') return null

  let values
  try {
    values = JSON.parse(serialized)
  } catch {
    return null
  }
  if (!Array.isArray(values)) return null

  const maximum = Number.parseInt(mediaCount, 10)
  if (!Number.isFinite(maximum) || maximum < 0) return null
  const positions = values.map((value) => Number(value))
  if (
    positions.some((position) => !Number.isInteger(position) || position < 1 || position > maximum)
  ) {
    return null
  }

  return [...new Set(positions)].sort((left, right) => left - right)
}

export function resolveGalleryFilterPresentation(mediaCount, filteredPositions) {
  const total = Math.max(Number.parseInt(mediaCount, 10) || 0, 0)
  const requestedPositions = [...(filteredPositions ?? [])]
  const filtered = [
    ...new Set(
      requestedPositions
        .map((position) => Number.parseInt(position, 10))
        .filter((position) => Number.isFinite(position) && position >= 1 && position <= total),
    ),
  ].sort((left, right) => left - right)
  const filteredSet = new Set(filtered)

  return {
    filteredPositions: filtered,
    filteredIndexes: filtered.map((position) => position - 1),
    serialized: JSON.stringify(filtered),
    visiblePositions: Array.from({ length: total }, (_, index) => index + 1).filter(
      (position) => !filteredSet.has(position),
    ),
  }
}

export function applyGalleryFilterPresentation(gallery, filteredPositions, { custom = true } = {}) {
  const mediaItems = [...(gallery?.querySelectorAll?.('.product-gallery__media') ?? [])]
  const presentation = resolveGalleryFilterPresentation(mediaItems.length, filteredPositions)
  gallery?.setAttribute?.('filtered-indexes', presentation.serialized)
  if (custom) {
    gallery?.setAttribute?.(
      'data-main-product-figma-applied-filtered-indexes',
      presentation.serialized,
    )
  } else {
    gallery?.removeAttribute?.('data-main-product-figma-applied-filtered-indexes')
    gallery?.removeAttribute?.('data-main-product-figma-impact-filtered-indexes')
  }

  mediaItems.forEach((item, index) => {
    item.hidden = presentation.filteredPositions.includes(index + 1)
  })
  for (const pageDots of gallery?.querySelectorAll?.('page-dots') ?? []) {
    ;[...pageDots.children].forEach((control, index) => {
      const filterHidden = presentation.filteredPositions.includes(index + 1)
      control.hidden = filterHidden
      if (pageDots.matches?.('.product-gallery__thumbnail-list')) {
        if (custom) {
          control.toggleAttribute(
            'data-main-product-figma-gallery-thumbnail-filter-hidden',
            filterHidden,
          )
        } else {
          control.removeAttribute('data-main-product-figma-gallery-thumbnail-filter-hidden')
        }
      }
    })
    pageDots.dispatchEvent(
      new CustomEvent('control:filter', {
        detail: { filteredIndexes: presentation.filteredIndexes },
      }),
    )
  }
  for (const carousel of gallery?.querySelectorAll?.('media-carousel') ?? []) {
    carousel.dispatchEvent(
      new CustomEvent('carousel:filter', {
        detail: { filteredIndexes: presentation.filteredIndexes },
      }),
    )
  }

  return presentation
}

export function restoreGalleryPresentation({
  gallery,
  controls,
  navigation,
  toggle,
  thumbnailPrevious,
  thumbnailNext,
  previewControl,
  nativeFilteredPositions = [],
  actualPosition,
  restoreVariantThumbnailAccessibility,
} = {}) {
  restoreVariantThumbnailAccessibility?.()
  const presentation = applyGalleryFilterPresentation(gallery, nativeFilteredPositions, {
    custom: false,
  })
  const currentState = resolveGalleryNavigationState(presentation.visiblePositions, actualPosition)
  for (const pageDots of gallery?.querySelectorAll?.('page-dots') ?? []) {
    ;[...pageDots.children].forEach((control, index) => {
      control.setAttribute('aria-current', String(index + 1 === currentState.currentPosition))
    })
  }

  for (const carousel of gallery?.querySelectorAll?.('media-carousel') ?? []) {
    carousel.removeAttribute('role')
    carousel.removeAttribute('aria-labelledby')
  }
  for (const button of controls?.querySelectorAll?.('[data-main-product-figma-gallery-filter]') ??
    []) {
    button.setAttribute('aria-selected', 'false')
    button.tabIndex = -1
  }
  controls?.setAttribute?.('hidden', '')
  navigation?.setAttribute?.('hidden', '')
  toggle?.setAttribute?.('hidden', '')
  toggle?.setAttribute?.('aria-expanded', 'false')
  toggle?.removeAttribute?.('aria-controls')
  thumbnailPrevious?.setAttribute?.('hidden', '')
  thumbnailNext?.setAttribute?.('hidden', '')

  if (previewControl) {
    previewControl.replaceChildren()
    previewControl.setAttribute('hidden', '')
    for (const attribute of [
      'aria-current',
      'aria-label',
      'data-main-product-figma-variant-thumbnail-current',
      'data-main-product-figma-variant-thumbnail-target',
    ]) {
      previewControl.removeAttribute(attribute)
    }
  }

  return presentation
}

export function createGalleryNativeFilterOwner() {
  const nativePositionsByGallery = new WeakMap()
  const readPositions = (gallery) =>
    parseGalleryFilteredPositions(
      gallery?.getAttribute?.('filtered-indexes'),
      gallery?.querySelectorAll?.('.product-gallery__media')?.length,
    )

  return {
    capture(gallery) {
      const existing = nativePositionsByGallery.get(gallery)
      if (existing) return [...existing]

      const positions = readPositions(gallery)
      if (positions === null) return null
      nativePositionsByGallery.set(gallery, positions)
      return [...positions]
    },
    get(gallery) {
      const positions = nativePositionsByGallery.get(gallery)
      return positions ? [...positions] : null
    },
    refreshFromRerender(gallery, htmlFragment) {
      const formIdentity = gallery?.getAttribute?.('form')
      if (!formIdentity || typeof htmlFragment?.querySelectorAll !== 'function') return false

      const matchingGallery = [...htmlFragment.querySelectorAll('product-gallery')].find(
        (candidate) => candidate.getAttribute?.('form') === formIdentity,
      )
      if (!matchingGallery) return false

      const positions = readPositions(matchingGallery)
      if (positions === null) return false
      nativePositionsByGallery.set(gallery, positions)
      return true
    },
    release(gallery) {
      nativePositionsByGallery.delete(gallery)
    },
  }
}

export function syncPdpBadgeBlocksFromRerender(root, htmlFragment) {
  if (
    typeof root?.querySelectorAll !== 'function' ||
    typeof htmlFragment?.querySelectorAll !== 'function'
  ) {
    return 0
  }

  const selector = '[data-block-type="pdp-badge"]'
  const incomingBlocks = new Map(
    [...htmlFragment.querySelectorAll(selector)]
      .map((block) => [block.getAttribute?.('data-block-id'), block])
      .filter(([blockId]) => blockId),
  )
  let replacements = 0

  for (const currentBlock of root.querySelectorAll(selector)) {
    const blockId = currentBlock.getAttribute?.('data-block-id')
    const incomingBlock = blockId ? incomingBlocks.get(blockId) : null
    if (!incomingBlock || typeof currentBlock.replaceWith !== 'function') continue

    currentBlock.replaceWith(incomingBlock)
    replacements += 1
  }

  return replacements
}

export function prioritizeVariantFeatureMedia(visiblePositions, variantMediaPosition) {
  const positions = [
    ...new Set(
      (visiblePositions ?? [])
        .map((position) => Number.parseInt(position, 10))
        .filter((position) => Number.isFinite(position) && position > 0),
    ),
  ]
  const preferred = Number.parseInt(variantMediaPosition, 10)
  if (!positions.includes(preferred)) return positions
  return [preferred, ...positions.filter((position) => position !== preferred)]
}

export function resolveGalleryNavigationState(
  visiblePositions,
  currentPosition,
  { preserveOrder = false } = {},
) {
  const positions = [
    ...new Set(
      (visiblePositions ?? [])
        .map((position) => Number.parseInt(position, 10))
        .filter((position) => Number.isFinite(position) && position > 0),
    ),
  ]
  if (!preserveOrder) positions.sort((left, right) => left - right)
  const requested = Number.parseInt(currentPosition, 10)
  const resolvedPosition = positions.includes(requested) ? requested : (positions[0] ?? null)
  const currentIndex = resolvedPosition === null ? -1 : positions.indexOf(resolvedPosition)

  return {
    currentPosition: resolvedPosition,
    current: currentIndex + 1,
    total: positions.length,
    previousPosition:
      positions.length > 1 && currentIndex >= 0
        ? positions[(currentIndex - 1 + positions.length) % positions.length]
        : null,
    nextPosition:
      positions.length > 1 && currentIndex >= 0
        ? positions[(currentIndex + 1) % positions.length]
        : null,
  }
}

export function resolveGalleryPresentedNavigationState(
  visiblePositions,
  currentPosition,
  { previewTargetPosition, previewVisible = false } = {},
) {
  return resolveGalleryNavigationState(
    previewVisible
      ? prioritizeVariantFeatureMedia(visiblePositions, previewTargetPosition)
      : visiblePositions,
    currentPosition,
    { preserveOrder: previewVisible },
  )
}

export function resolveGalleryPointerWrapTarget({
  clientX,
  currentPosition,
  left,
  visiblePositions,
  width,
} = {}) {
  const pointerX = Number(clientX)
  const originX = Number(left)
  const viewportWidth = Number(width)
  if (
    !Number.isFinite(pointerX) ||
    !Number.isFinite(originX) ||
    !Number.isFinite(viewportWidth) ||
    viewportWidth <= 0
  ) {
    return null
  }

  const state = resolveGalleryNavigationState(visiblePositions, currentPosition)
  if (state.total <= 1) return null
  return pointerX - originX > viewportWidth / 2 ? state.nextPosition : state.previousPosition
}

export function resolveGalleryKeyboardWrapTarget({
  currentPosition,
  direction = 'ltr',
  key,
  visiblePositions,
} = {}) {
  if (!['ArrowLeft', 'ArrowRight'].includes(key)) return null

  const state = resolveGalleryNavigationState(visiblePositions, currentPosition)
  if (state.total <= 1) return null

  const movesNext = direction === 'rtl' ? key === 'ArrowLeft' : key === 'ArrowRight'
  return movesNext ? state.nextPosition : state.previousPosition
}

export function resolveGallerySwipeWrapTarget({
  currentPosition,
  endX,
  endY,
  startX,
  startY,
  threshold = 40,
  visiblePositions,
} = {}) {
  const movement = Number(endX) - Number(startX)
  const verticalMovement = Number(endY) - Number(startY)
  const minimumMovement = Math.max(Number(threshold) || 40, 1)
  if (!Number.isFinite(movement) || Math.abs(movement) < minimumMovement) return null
  if (Number.isFinite(verticalMovement) && Math.abs(verticalMovement) > Math.abs(movement)) {
    return null
  }

  const state = resolveGalleryNavigationState(visiblePositions, currentPosition)
  if (state.total <= 1) return null
  if (movement < 0 && state.current === state.total) return state.nextPosition
  if (movement > 0 && state.current === 1) return state.previousPosition
  return null
}

export function resolveGalleryBoundarySwipeIntent({
  currentPosition,
  deadzone = 4,
  dominance = 1.25,
  endX,
  endY,
  startX,
  startY,
  visiblePositions,
} = {}) {
  const movement = Number(endX) - Number(startX)
  const rawVerticalMovement = Number(endY) - Number(startY)
  const verticalMovement = Number.isFinite(rawVerticalMovement) ? rawVerticalMovement : 0
  if (!Number.isFinite(movement)) return 'pending'

  const minimumMovement = Math.max(Number(deadzone) || 4, 1)
  const horizontalDominance = Math.max(Number(dominance) || 1.25, 1)
  const horizontalDistance = Math.abs(movement)
  const verticalDistance = Math.abs(verticalMovement)
  if (Math.max(horizontalDistance, verticalDistance) < minimumMovement) return 'pending'
  if (
    horizontalDistance < minimumMovement ||
    horizontalDistance <= verticalDistance * horizontalDominance
  ) {
    return 'native'
  }

  return resolveGallerySwipeWrapTarget({
    currentPosition,
    endX,
    endY,
    startX,
    startY,
    threshold: minimumMovement,
    visiblePositions,
  }) === null
    ? 'native'
    : 'boundary'
}

export function shouldOwnGalleryBoundarySwipe(options = {}) {
  return resolveGalleryBoundarySwipeIntent(options) === 'boundary'
}

export function resolveGallerySelectionAnimation({
  currentPosition,
  reducedMotion = false,
  targetPosition,
  visiblePositions = [],
} = {}) {
  if (reducedMotion === true) return false

  const positions = [
    ...new Set(
      (visiblePositions ?? [])
        .map((position) => Number.parseInt(position, 10))
        .filter((position) => Number.isFinite(position) && position > 0),
    ),
  ].sort((left, right) => left - right)
  const currentIndex = positions.indexOf(Number.parseInt(currentPosition, 10))
  const targetIndex = positions.indexOf(Number.parseInt(targetPosition, 10))

  return currentIndex >= 0 && targetIndex >= 0 && Math.abs(targetIndex - currentIndex) === 1
}

export function resolveGalleryThumbnailPosition(thumbnails, target) {
  const index = [...(thumbnails ?? [])].indexOf(target)
  return index >= 0 ? index + 1 : null
}

export function resolveGalleryThumbnailWindowStart(
  visibleCount,
  limit,
  windowStartIndex,
  direction,
) {
  const total = Math.max(Number.parseInt(visibleCount, 10) || 0, 0)
  const maximum = Math.max(Number.parseInt(limit, 10) || 1, 1)
  const maximumWindowStart = Math.max(total - maximum, 0)
  if (maximumWindowStart === 0) return 0

  const current = Math.min(
    Math.max(Number.parseInt(windowStartIndex, 10) || 0, 0),
    maximumWindowStart,
  )
  if (direction === 'previous') {
    return current === 0 ? maximumWindowStart : current - 1
  }
  return current === maximumWindowStart ? 0 : current + 1
}

const GALLERY_AUTOPLAY_CONFIGURED_ATTRIBUTE = 'data-main-product-pdp-autoplay-configured'
const GALLERY_LOOP_CONFIGURED_ATTRIBUTE = 'data-main-product-pdp-loop-configured'
const GALLERY_VIDEO_TAB_AUTOPLAY_ATTRIBUTE = 'data-main-product-pdp-video-tab-autoplay'

function releaseGalleryVideoTabAutoplay(player) {
  if (player?.hasAttribute?.(GALLERY_VIDEO_TAB_AUTOPLAY_ATTRIBUTE) !== true) return false

  player.removeAttribute?.('autoplay')
  player.removeAttribute?.(GALLERY_VIDEO_TAB_AUTOPLAY_ATTRIBUTE)
  return true
}

export function syncGalleryVideoPlayback({ activeGroup, gallery, reducedMotion = false } = {}) {
  const players = [...(gallery?.querySelectorAll?.('video-media') ?? [])]
  for (const player of players) {
    player.pause?.()
    releaseGalleryVideoTabAutoplay(player)
  }

  if (!gallery) return { playRequested: false, reason: 'gallery-unavailable' }
  if (activeGroup !== 'videos') return { playRequested: false, reason: 'inactive-group' }
  if (reducedMotion === true) return { playRequested: false, reason: 'reduced-motion' }

  const activeSlide = [
    ...(gallery.querySelectorAll?.('.product-gallery__media:not([hidden])') ?? []),
  ].find((slide) => ['video', 'external_video'].includes(slide.getAttribute?.('data-media-type')))
  const directPlayer = activeSlide?.firstElementChild
  const player = directPlayer?.matches?.('video-media')
    ? directPlayer
    : activeSlide?.querySelector?.(':scope > video-media')
  if (!player || typeof player.play !== 'function') {
    return { playRequested: false, reason: 'visible-video-unavailable' }
  }

  player.setAttribute?.(GALLERY_VIDEO_TAB_AUTOPLAY_ATTRIBUTE, '')
  player.setAttribute?.('autoplay', '')
  for (const video of player.querySelectorAll?.('video') ?? []) {
    video.muted = true
    video.defaultMuted = true
    video.setAttribute?.('muted', '')
  }

  try {
    const playResult = player.play()
    playResult?.catch?.(() => undefined)
  } catch {
    releaseGalleryVideoTabAutoplay(player)
    return { playRequested: false, reason: 'play-rejected' }
  }
  releaseGalleryVideoTabAutoplay(player)

  return { playRequested: true, reason: 'video-tab' }
}

export function applyGalleryMotionPreference(gallery, reducedMotion = false) {
  const carousel = gallery?.querySelector?.('media-carousel')
  const mediaPlayers = [...(gallery?.querySelectorAll?.('video-media, model-media') ?? [])]
  const reduced = reducedMotion === true

  if (carousel?.hasAttribute?.('autoplay')) {
    carousel.setAttribute(GALLERY_AUTOPLAY_CONFIGURED_ATTRIBUTE, '')
  }
  const autoplayConfigured =
    carousel?.hasAttribute?.(GALLERY_AUTOPLAY_CONFIGURED_ATTRIBUTE) === true
  carousel?.toggleAttribute?.('autoplay', autoplayConfigured && !reduced)

  let loopedVideoCount = 0
  for (const player of mediaPlayers) {
    for (const video of player.querySelectorAll?.('video') ?? []) {
      if (video.hasAttribute?.('loop')) {
        video.setAttribute(GALLERY_LOOP_CONFIGURED_ATTRIBUTE, '')
      }
      const loopConfigured = video.hasAttribute?.(GALLERY_LOOP_CONFIGURED_ATTRIBUTE) === true
      if (loopConfigured) loopedVideoCount += 1
      video.toggleAttribute?.('loop', loopConfigured && !reduced)
    }
    if (reduced) {
      player.pause?.()
      releaseGalleryVideoTabAutoplay(player)
    }
  }

  return { autoplayConfigured, loopedVideoCount, reducedMotion: reduced }
}

export function restoreGalleryMotionPreference(gallery) {
  const carousel = gallery?.querySelector?.('media-carousel')
  if (carousel?.hasAttribute?.(GALLERY_AUTOPLAY_CONFIGURED_ATTRIBUTE)) {
    carousel.setAttribute('autoplay', '')
    carousel.removeAttribute(GALLERY_AUTOPLAY_CONFIGURED_ATTRIBUTE)
  }

  for (const player of gallery?.querySelectorAll?.('video-media') ?? []) {
    releaseGalleryVideoTabAutoplay(player)
    for (const video of player.querySelectorAll?.('video') ?? []) {
      if (!video.hasAttribute?.(GALLERY_LOOP_CONFIGURED_ATTRIBUTE)) continue
      video.setAttribute('loop', '')
      video.removeAttribute(GALLERY_LOOP_CONFIGURED_ATTRIBUTE)
    }
  }
}

export function suppressGallerySettleAutoplay({ event, reducedMotion = false, root } = {}) {
  if (reducedMotion !== true) return null

  const carousel = event?.target
  const gallery = carousel?.closest?.('product-gallery')
  const player = event?.detail?.slide?.firstElementChild
  if (
    !carousel?.matches?.('media-carousel') ||
    !gallery ||
    !root?.contains?.(gallery) ||
    !player?.matches?.('video-media, model-media') ||
    typeof player.play !== 'function'
  ) {
    return null
  }

  const originalDescriptor = Object.getOwnPropertyDescriptor(player, 'play')
  const suppressedPlay = () => undefined
  try {
    Object.defineProperty(player, 'play', {
      configurable: true,
      value: suppressedPlay,
      writable: true,
    })
  } catch {
    return null
  }

  let restored = false
  return () => {
    if (restored) return
    restored = true
    if (player.play !== suppressedPlay) return
    if (originalDescriptor) Object.defineProperty(player, 'play', originalDescriptor)
    else delete player.play
  }
}

export function resolveGalleryCounterPresentation(
  state,
  { previewTargetPosition, previewVisible = false, visiblePositions = [] } = {},
) {
  const presentationPositions = previewVisible
    ? prioritizeVariantFeatureMedia(visiblePositions, previewTargetPosition)
    : []
  const currentPosition = Number.parseInt(state?.currentPosition, 10)
  const presentationIndex = presentationPositions.indexOf(currentPosition)

  if (presentationIndex >= 0) {
    return {
      current: presentationIndex + 1,
      total: presentationPositions.length,
    }
  }

  return {
    current: state?.current,
    total: state?.total,
  }
}

export function resolveGallerySelectionTransition({
  actualPosition,
  preferredPosition,
  visiblePositions,
} = {}) {
  const positions = [
    ...new Set(
      (visiblePositions ?? [])
        .map((position) => Number.parseInt(position, 10))
        .filter((position) => Number.isFinite(position) && position > 0),
    ),
  ].sort((left, right) => left - right)
  const actual = Number.parseInt(actualPosition, 10)
  const normalizedActual = Number.isFinite(actual) && actual > 0 ? actual : null
  const preferred = Number.parseInt(preferredPosition, 10)
  const requestedPosition = positions.includes(preferred) ? preferred : normalizedActual
  const finalPosition = positions.includes(requestedPosition)
    ? requestedPosition
    : (positions[0] ?? null)

  return {
    actualPosition: normalizedActual,
    finalPosition,
    shouldSelect: finalPosition !== null && finalPosition !== normalizedActual,
  }
}

export function galleryTabIndexForKey(currentIndex, itemCount, key, direction = 'ltr') {
  const count = Number.parseInt(itemCount, 10)
  if (!Number.isFinite(count) || count < 1) return null

  const current = Math.min(Math.max(Number.parseInt(currentIndex, 10) || 0, 0), count - 1)
  const inlineNext = direction === 'rtl' ? -1 : 1
  if (key === 'ArrowRight') return (current + inlineNext + count) % count
  if (key === 'ArrowLeft') return (current - inlineNext + count) % count
  if (key === 'Home') return 0
  if (key === 'End') return count - 1
  return null
}

export function resolveGalleryThumbnailState(
  visiblePositions,
  expanded = false,
  limit = 7,
  currentPosition = null,
  windowStartIndex = null,
  followCurrent = true,
) {
  const positions = [
    ...new Set(
      (visiblePositions ?? [])
        .map((position) => Number.parseInt(position, 10))
        .filter((position) => Number.isFinite(position) && position > 0),
    ),
  ].sort((left, right) => left - right)
  const maximum = Math.max(Number.parseInt(limit, 10) || 7, 1)
  const requestedPosition = Number.parseInt(currentPosition, 10)
  const currentIndex = positions.indexOf(requestedPosition)
  const maximumWindowStart = Math.max(positions.length - maximum, 0)
  const requestedWindowStart = Number.parseInt(windowStartIndex, 10)
  let windowStart = Number.isFinite(requestedWindowStart)
    ? Math.min(Math.max(requestedWindowStart, 0), maximumWindowStart)
    : 0

  if (followCurrent === true && currentIndex >= 0) {
    if (currentIndex < windowStart) windowStart = currentIndex
    if (currentIndex >= windowStart + maximum) windowStart = currentIndex - maximum + 1
    windowStart = Math.min(Math.max(windowStart, 0), maximumWindowStart)
  }

  const windowEnd = Math.min(positions.length, windowStart + maximum)
  const remaining = Math.max(positions.length - windowEnd, 0)
  const isExpanded = positions.length > maximum && expanded === true
  const shownPositions = isExpanded ? positions : positions.slice(windowStart, windowEnd)
  const shown = new Set(shownPositions)

  return {
    canNext: !isExpanded && windowEnd < positions.length,
    canPrevious: !isExpanded && windowStart > 0,
    expanded: isExpanded,
    hasToggle: remaining > 0,
    remaining,
    shownPositions,
    hiddenPositions: positions.filter((position) => !shown.has(position)),
    windowEndIndex: windowEnd,
    windowStartIndex: windowStart,
  }
}

export function resolveVariantThumbnailCapState(
  visiblePositions,
  expanded = false,
  _proxiedPosition = null,
  limit = 7,
  currentPosition = null,
  windowStartIndex = null,
  followCurrent = true,
) {
  // Keep the legacy argument position for existing callers; V15 gives the
  // variant preview its own slot, so it no longer changes native rail math.
  void _proxiedPosition
  return resolveGalleryThumbnailState(
    visiblePositions,
    expanded,
    limit,
    currentPosition,
    windowStartIndex,
    followCurrent,
  )
}

export function resolveVariantFeaturedPreview({
  canonicalVariantId,
  filteredPositions = [],
  graphVariantId,
  mediaItems = [],
  pending = false,
  variant,
} = {}) {
  const canonicalId = toId(canonicalVariantId)
  const graphId = toId(graphVariantId)
  const variantId = toId(variant?.id)
  const imageSrc = typeof variant?.featuredImage === 'string' ? variant.featuredImage.trim() : ''
  if (
    pending === true ||
    !canonicalId ||
    canonicalId !== graphId ||
    canonicalId !== variantId ||
    !imageSrc
  ) {
    return null
  }

  const featuredMediaId = toId(variant?.featuredMediaId)
  let targetPosition
  if (featuredMediaId) {
    targetPosition =
      mediaItems.findIndex(
        (item) => toId(item?.getAttribute?.('data-media-id')) === featuredMediaId,
      ) + 1
    if (targetPosition < 1) return null
  } else {
    const configuredPosition = Number.parseInt(variant?.featuredMediaPosition, 10)
    targetPosition =
      Number.isFinite(configuredPosition) &&
      configuredPosition > 0 &&
      configuredPosition <= mediaItems.length
        ? configuredPosition
        : 0
  }
  const targetMedia = targetPosition > 0 ? mediaItems[targetPosition - 1] : null
  const filtered = new Set(
    (filteredPositions ?? [])
      .map((position) => Number.parseInt(position, 10))
      .filter((position) => Number.isFinite(position) && position > 0),
  )
  if (
    !targetMedia ||
    targetMedia.getAttribute?.('data-media-type') !== 'image' ||
    filtered.has(targetPosition)
  ) {
    return null
  }

  return {
    imageSrc,
    label: String(variant?.title ?? '').trim(),
    targetPosition,
  }
}

function rememberVariantThumbnailState(thumbnail) {
  if (thumbnail.hasAttribute('data-main-product-figma-variant-thumbnail-previous-hidden')) {
    return
  }

  thumbnail.setAttribute(
    'data-main-product-figma-variant-thumbnail-previous-hidden',
    String(Boolean(thumbnail.hidden)),
  )
  thumbnail.setAttribute(
    'data-main-product-figma-variant-thumbnail-previous-inert',
    String(thumbnail.hasAttribute('inert')),
  )
  const previousAriaHidden = thumbnail.getAttribute('aria-hidden')
  if (previousAriaHidden !== null) {
    thumbnail.setAttribute(
      'data-main-product-figma-variant-thumbnail-previous-aria-hidden',
      previousAriaHidden,
    )
  }
}

function suppressVariantThumbnail(thumbnail, marker, { hidden = false } = {}) {
  if (!thumbnail) return

  rememberVariantThumbnailState(thumbnail)
  thumbnail.setAttribute(marker, '')
  thumbnail.hidden = hidden
  thumbnail.setAttribute('inert', '')
  thumbnail.setAttribute('aria-hidden', 'true')
  thumbnail.removeAttribute('aria-current')
}

export function clearVariantThumbnailPreview({ previewControl, thumbnails = [] } = {}) {
  for (const thumbnail of thumbnails) {
    if (
      !thumbnail.hasAttribute('data-main-product-figma-variant-thumbnail-obscured') &&
      !thumbnail.hasAttribute('data-main-product-figma-variant-thumbnail-proxied')
    ) {
      continue
    }

    const previousHidden = thumbnail.getAttribute(
      'data-main-product-figma-variant-thumbnail-previous-hidden',
    )
    if (previousHidden !== null) thumbnail.hidden = previousHidden === 'true'

    const previousInert = thumbnail.getAttribute(
      'data-main-product-figma-variant-thumbnail-previous-inert',
    )
    if (previousInert === 'true') thumbnail.setAttribute('inert', '')
    else if (previousInert !== null) thumbnail.removeAttribute('inert')

    const previousAriaHiddenAttribute =
      'data-main-product-figma-variant-thumbnail-previous-aria-hidden'
    if (thumbnail.hasAttribute(previousAriaHiddenAttribute)) {
      thumbnail.setAttribute(
        'aria-hidden',
        thumbnail.getAttribute(previousAriaHiddenAttribute) ?? '',
      )
    } else if (previousInert !== null) {
      thumbnail.removeAttribute('aria-hidden')
    }

    for (const attribute of [
      'data-main-product-figma-variant-thumbnail-obscured',
      'data-main-product-figma-variant-thumbnail-proxied',
      'data-main-product-figma-variant-thumbnail-previous-hidden',
      'data-main-product-figma-variant-thumbnail-previous-inert',
      'data-main-product-figma-variant-thumbnail-previous-aria-hidden',
    ]) {
      thumbnail.removeAttribute(attribute)
    }
  }

  if (previewControl) {
    previewControl.replaceChildren()
    previewControl.setAttribute('hidden', '')
    for (const attribute of [
      'aria-current',
      'aria-label',
      'data-main-product-figma-variant-thumbnail-current',
      'data-main-product-figma-variant-thumbnail-target',
    ]) {
      previewControl.removeAttribute(attribute)
    }
  }

  return { obscuredPosition: null, proxiedPosition: null, visible: false }
}

export function applyVariantThumbnailPreview({
  documentRef = globalThis.document,
  previewControl,
  resolution,
  thumbnails = [],
} = {}) {
  clearVariantThumbnailPreview({ previewControl, thumbnails })
  if (!previewControl || !resolution || thumbnails.length < 2) {
    return { obscuredPosition: null, proxiedPosition: null, visible: false }
  }

  const targetPosition = Number.parseInt(resolution.targetPosition, 10)
  if (targetPosition < 1 || targetPosition > thumbnails.length) {
    return { obscuredPosition: null, proxiedPosition: null, visible: false }
  }

  const target = thumbnails[targetPosition - 1]
  if (!target || target.hasAttribute('data-main-product-figma-gallery-thumbnail-filter-hidden')) {
    return { obscuredPosition: null, proxiedPosition: null, visible: false }
  }

  const image = documentRef?.createElement?.('img')
  if (!image) return { obscuredPosition: null, proxiedPosition: null, visible: false }
  image.setAttribute('src', resolution.imageSrc)
  image.setAttribute('width', '68')
  image.setAttribute('height', '68')
  image.setAttribute('alt', '')

  const moveFocus = documentRef?.activeElement === target
  suppressVariantThumbnail(target, 'data-main-product-figma-variant-thumbnail-proxied', {
    hidden: true,
  })
  previewControl.replaceChildren(image)
  const nativeLabel = target.getAttribute('aria-label')?.trim()
  const accessibleLabel = nativeLabel || String(resolution.label ?? '').trim()
  if (accessibleLabel) previewControl.setAttribute('aria-label', accessibleLabel)
  previewControl.setAttribute(
    'data-main-product-figma-variant-thumbnail-target',
    String(targetPosition),
  )
  previewControl.removeAttribute('hidden')
  if (moveFocus) previewControl.focus({ preventScroll: true })

  return {
    obscuredPosition: null,
    proxiedPosition: targetPosition,
    visible: true,
  }
}

function canFocusVariantThumbnail(thumbnail) {
  return Boolean(
    thumbnail &&
    !thumbnail.hidden &&
    !thumbnail.hasAttribute('inert') &&
    thumbnail.getAttribute('aria-hidden') !== 'true',
  )
}

export function reconcileVariantThumbnailPresentation({
  currentPosition,
  documentRef = globalThis.document,
  expanded = false,
  followCurrent = true,
  previewControl,
  resolution,
  thumbnailLimit = 7,
  thumbnails = [],
  visiblePositions = [],
  windowStartIndex = null,
} = {}) {
  const previousTargetPosition = Number.parseInt(
    previewControl?.getAttribute('data-main-product-figma-variant-thumbnail-target'),
    10,
  )
  const previewWasFocused = documentRef?.activeElement === previewControl
  const resolutionTargetPosition = Number.parseInt(resolution?.targetPosition, 10)
  const normalizedVisiblePositions = [
    ...new Set(
      (visiblePositions ?? [])
        .map((position) => Number.parseInt(position, 10))
        .filter((position) => Number.isFinite(position) && position > 0),
    ),
  ].sort((left, right) => left - right)
  const projectionTargetPosition =
    thumbnails.length > 1 && normalizedVisiblePositions.includes(resolutionTargetPosition)
      ? resolutionTargetPosition
      : null
  const thumbnailState = resolveVariantThumbnailCapState(
    normalizedVisiblePositions,
    expanded,
    projectionTargetPosition,
    thumbnailLimit,
    currentPosition,
    windowStartIndex,
    followCurrent,
  )
  const projection = applyVariantThumbnailPreview({
    documentRef,
    previewControl,
    resolution: projectionTargetPosition === null ? null : resolution,
    thumbnails,
  })
  thumbnails.forEach((thumbnail) => {
    thumbnail.hidden =
      thumbnail.hasAttribute('data-main-product-figma-gallery-thumbnail-filter-hidden') ||
      thumbnail.hasAttribute('data-main-product-figma-variant-thumbnail-proxied')
  })
  const ownership = applyVariantThumbnailCurrentPresentation({
    currentPosition,
    previewControl,
    thumbnails,
  })

  if (previewWasFocused) {
    const restoredTarget = Number.isFinite(previousTargetPosition)
      ? thumbnails[previousTargetPosition - 1]
      : null
    const focusTarget = projection.visible
      ? previewControl
      : canFocusVariantThumbnail(restoredTarget)
        ? restoredTarget
        : thumbnails.find(canFocusVariantThumbnail)
    focusTarget?.focus({ preventScroll: true })
  }

  return { ownership, projection, thumbnailState }
}

export function resolveVariantThumbnailCurrentState({
  currentPosition,
  previewTargetPosition,
  obscuredPosition,
  previewVisible = false,
} = {}) {
  const current = Number.parseInt(currentPosition, 10)
  const target = Number.parseInt(previewTargetPosition, 10)
  const obscured = Number.parseInt(obscuredPosition, 10)
  if (!Number.isFinite(current) || current < 1) {
    return { previewCurrent: false, nativeCurrentPosition: null }
  }

  const previewCurrent = previewVisible === true && Number.isFinite(target) && target === current
  const currentIsObscured =
    previewVisible === true && Number.isFinite(obscured) && obscured === current

  return {
    previewCurrent,
    nativeCurrentPosition: previewCurrent || currentIsObscured ? null : current,
  }
}

export function applyVariantThumbnailCurrentPresentation({
  currentPosition,
  previewControl,
  thumbnails = [],
} = {}) {
  const obscuredIndex = thumbnails.findIndex((thumbnail) =>
    thumbnail.hasAttribute('data-main-product-figma-variant-thumbnail-obscured'),
  )
  const ownership = resolveVariantThumbnailCurrentState({
    currentPosition,
    previewTargetPosition: previewControl?.getAttribute(
      'data-main-product-figma-variant-thumbnail-target',
    ),
    obscuredPosition: obscuredIndex >= 0 ? obscuredIndex + 1 : null,
    previewVisible: Boolean(previewControl && !previewControl.hidden),
  })

  thumbnails.forEach((thumbnail, index) => {
    const nativeCurrent =
      ownership.nativeCurrentPosition === index + 1 &&
      !thumbnail.hidden &&
      thumbnail.getAttribute('aria-hidden') !== 'true'
    if (nativeCurrent) thumbnail.setAttribute('aria-current', 'true')
    else thumbnail.removeAttribute('aria-current')
  })

  previewControl?.toggleAttribute(
    'data-main-product-figma-variant-thumbnail-current',
    ownership.previewCurrent,
  )
  if (ownership.previewCurrent) previewControl?.setAttribute('aria-current', 'true')
  else previewControl?.removeAttribute('aria-current')
  return ownership
}

export function createCartPayload(graph, { sections, sectionsUrl } = {}) {
  if (graph?.hasUnsupportedLineItemProperty) {
    const error = new Error('Bundled cart payload cannot include a file-valued property.')
    error.code = 'UNSUPPORTED_LINE_ITEM_PROPERTY'
    throw error
  }

  if (!graph?.mainVariantId || graph.mainVariantAvailable !== true) {
    throw new Error('An available main variant is required before cart submission.')
  }

  const main = {
    id: graph.mainVariantId,
    quantity: positiveInteger(graph.mainQuantity),
  }
  if (graph.sellingPlanId) main.selling_plan = graph.sellingPlanId
  if (Object.keys(graph.lineItemProperties ?? {}).length > 0) {
    main.properties = cloneProperties(graph.lineItemProperties)
  }

  const payload = {
    items: [
      main,
      ...selectedAddonStates(graph).map((state) => ({
        id: state.resolvedVariantId,
        quantity: positiveInteger(state.quantity),
      })),
    ],
  }

  if (Array.isArray(sections) && sections.length > 0) payload.sections = sections
  if (sectionsUrl) payload.sections_url = sectionsUrl
  return payload
}

function cartRequestError(code, { cartMayHaveChanged = false, customerMessage = '', cause } = {}) {
  const error = new Error(code)
  error.code = code
  error.cartMayHaveChanged = cartMayHaveChanged === true
  error.customerMessage = customerMessage
  if (cause !== undefined) error.cause = cause
  return error
}

const STOREFRONT_API_VERSION = '2026-07'
const SELECTION_CHECKOUT_MUTATION = `
  mutation MainProductSelectionCheckout($input: CartInput!, $country: CountryCode!)
  @inContext(country: $country) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`

function shopifyGid(type, value) {
  const id = toId(value)?.trim()
  if (!id) return null
  const prefix = `gid://shopify/${type}/`
  if (id.startsWith(prefix) && /^\d+$/u.test(id.slice(prefix.length))) return id
  return /^\d+$/u.test(id) ? `${prefix}${id}` : null
}

function normalizedCountryCode(value) {
  const countryCode = String(value ?? '')
    .trim()
    .toUpperCase()
  return /^[A-Z]{2}$/u.test(countryCode) ? countryCode : null
}

function normalizedShopDomain(value) {
  const domain = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//u, '')
    .split('/')[0]
  return /^[a-z0-9][a-z0-9.-]*\.myshopify\.com$/u.test(domain) ? domain : null
}

function normalizeCheckoutDiscountCodes(values) {
  const codes = new Map()
  for (const value of Array.isArray(values) ? values : []) {
    if (typeof value !== 'string' || !value.trim()) continue
    const code = value.trim()
    if (!codes.has(code.toLowerCase())) codes.set(code.toLowerCase(), code)
  }
  if (codes.size > 250) throw cartRequestError('CHECKOUT_DISCOUNT_CODES_LIMIT')
  return [...codes.values()]
}

export async function resolveBogoCheckoutDiscountCodes({
  documentRef = globalThis.document,
  windowRef = globalThis.window,
  signal,
  timeoutMs = 3000,
} = {}) {
  const deadline = Date.now() + timeoutMs
  while (true) {
    throwIfSignalAborted(signal)
    const runtime = windowRef?.FGSECOMAPP
    if (!runtime) return []
    // The app initializes discounts to [] before loading its discount module.
    // offer_codes is created by that module; its synchronous init then filters
    // offers by schedule, customer and market before its first async operation.
    if (runtime?.variables?.discounts?.offer_codes && Array.isArray(runtime.discounts)) {
      return normalizeCheckoutDiscountCodes(
        runtime.discounts
          .filter((offer) => ['cart', 'volume', 'cheapest', 'product'].includes(offer?.type))
          .map((offer) => offer.code),
      )
    }
    if (Array.isArray(windowRef?.fgData?.discounts) && windowRef.fgData.discounts.length === 0) {
      return []
    }
    if (!documentRef?.querySelector?.('#bogos-discount-script')) return []
    if (Date.now() >= deadline) throw cartRequestError('CHECKOUT_DISCOUNTS_NOT_READY')

    await new Promise((resolve, reject) => {
      const cleanup = () => signal?.removeEventListener('abort', onAbort)
      const onAbort = () => {
        clearTimeout(timer)
        cleanup()
        try {
          throwIfSignalAborted(signal)
        } catch (error) {
          reject(error)
        }
      }
      const timer = setTimeout(
        () => {
          cleanup()
          resolve()
        },
        Math.min(50, deadline - Date.now()),
      )
      signal?.addEventListener('abort', onAbort, { once: true })
    })
  }
}

export function createStorefrontCartInput(graph, { countryCode, discountCodes } = {}) {
  const payload = createCartPayload(graph)
  const normalizedCountry = normalizedCountryCode(countryCode)
  if (!normalizedCountry) throw cartRequestError('CHECKOUT_CART_COUNTRY_INVALID')

  const lines = payload.items.map((item) => {
    const merchandiseId = shopifyGid('ProductVariant', item.id)
    if (!merchandiseId) throw cartRequestError('CHECKOUT_CART_VARIANT_ID_INVALID')

    const line = {
      merchandiseId,
      quantity: positiveInteger(item.quantity),
    }
    if (item.selling_plan) {
      const sellingPlanId = shopifyGid('SellingPlan', item.selling_plan)
      if (!sellingPlanId) throw cartRequestError('CHECKOUT_CART_SELLING_PLAN_ID_INVALID')
      line.sellingPlanId = sellingPlanId
    }
    if (item.properties && Object.keys(item.properties).length > 0) {
      line.attributes = Object.entries(item.properties).map(([key, value]) => ({
        key,
        value: String(value),
      }))
    }
    return line
  })

  const codes = normalizeCheckoutDiscountCodes(discountCodes)
  return {
    lines,
    buyerIdentity: { countryCode: normalizedCountry },
    ...(codes.length > 0 ? { discountCodes: codes } : {}),
  }
}

export async function requestSelectionCheckout({
  countryCode,
  discountCodes,
  fetchImpl = globalThis.fetch,
  graph,
  shopDomain,
  signal,
} = {}) {
  throwIfSignalAborted(signal)
  const domain = normalizedShopDomain(shopDomain)
  if (!domain) throw cartRequestError('CHECKOUT_CART_DOMAIN_INVALID')
  const input = createStorefrontCartInput(graph, { countryCode, discountCodes })
  const country = input.buyerIdentity.countryCode
  const endpoint = `https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`
  let response

  try {
    response = await fetchImpl(endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal,
      body: JSON.stringify({
        query: SELECTION_CHECKOUT_MUTATION,
        variables: { input, country },
      }),
    })
  } catch (error) {
    if (error?.name === 'AbortError') throw error
    throw cartRequestError('CHECKOUT_CART_NETWORK', { cause: error })
  }
  throwIfSignalAborted(signal)

  let responseJson
  try {
    responseJson = await response.json()
  } catch (error) {
    throw cartRequestError('CHECKOUT_CART_RESPONSE_INVALID', { cause: error })
  }
  throwIfSignalAborted(signal)

  if (!response.ok) {
    throw cartRequestError('CHECKOUT_CART_HTTP_REJECTED')
  }
  const graphQLErrors = Array.isArray(responseJson?.errors) ? responseJson.errors : []
  if (graphQLErrors.length > 0) {
    throw cartRequestError('CHECKOUT_CART_GRAPHQL_REJECTED', {
      customerMessage: String(graphQLErrors[0]?.message ?? '').trim(),
    })
  }

  const payload = responseJson?.data?.cartCreate
  const userErrors = Array.isArray(payload?.userErrors) ? payload.userErrors : []
  if (userErrors.length > 0) {
    throw cartRequestError('CHECKOUT_CART_USER_ERROR', {
      customerMessage: String(userErrors[0]?.message ?? '').trim(),
    })
  }

  const cart = payload?.cart
  let checkoutUrl
  try {
    checkoutUrl = new URL(cart?.checkoutUrl)
  } catch {
    throw cartRequestError('CHECKOUT_CART_URL_INVALID')
  }
  if (checkoutUrl.protocol !== 'https:') {
    throw cartRequestError('CHECKOUT_CART_URL_INVALID')
  }

  return {
    cartId: cart?.id ?? null,
    checkoutUrl: checkoutUrl.href,
  }
}

export async function requestCartAdd({
  graph,
  fetchImpl = globalThis.fetch,
  routesRoot = '/',
  sections,
  sectionsUrl,
  signal,
} = {}) {
  const payload = createCartPayload(graph, { sections, sectionsUrl })
  const normalizedRoot = String(routesRoot || '/').endsWith('/')
    ? String(routesRoot || '/')
    : `${routesRoot}/`
  let response

  try {
    response = await fetchImpl(`${normalizedRoot}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      signal,
      body: JSON.stringify(payload),
    })
  } catch (error) {
    if (error?.name === 'AbortError') throw error
    throw cartRequestError('CART_ADD_NETWORK', { cause: error })
  }

  let responseJson
  const cartMayHaveChanged = response.ok || response.status === 422
  try {
    responseJson = await response.json()
  } catch (error) {
    throw cartRequestError('CART_ADD_RESPONSE_INVALID', {
      cartMayHaveChanged,
      cause: error,
    })
  }

  if (!responseJson || typeof responseJson !== 'object') {
    throw cartRequestError('CART_ADD_RESPONSE_INVALID', {
      cartMayHaveChanged,
    })
  }

  if (!response.ok) {
    throw cartRequestError('CART_ADD_REJECTED', {
      cartMayHaveChanged: response.status === 422 || Number(responseJson.status) === 422,
      customerMessage:
        typeof responseJson.description === 'string' ? responseJson.description.trim() : '',
    })
  }

  return { responseJson }
}

export async function submitSelectionIntent({
  cartType,
  countryCode,
  documentRef = globalThis.document,
  fetchImpl = globalThis.fetch,
  form,
  graph,
  intent,
  locationRef = globalThis.window?.location,
  pageType,
  routesRoot = globalThis.window?.Shopify?.routes?.root ?? '/',
  shopDomain,
  signal,
  windowRef = globalThis.window,
} = {}) {
  if (!['add', 'shop-now'].includes(intent)) {
    throw cartRequestError('CART_INTENT_INVALID')
  }

  if (intent === 'shop-now') {
    const discountCodes = await resolveBogoCheckoutDiscountCodes({ documentRef, windowRef, signal })
    const checkout = await requestSelectionCheckout({
      countryCode,
      discountCodes,
      fetchImpl,
      graph,
      shopDomain,
      signal,
    })
    throwIfSignalAborted(signal)
    locationRef.href = checkout.checkoutUrl
    return { cart: null, checkout, destination: 'checkout', responseJson: null }
  }

  const normalizedRoot = String(routesRoot || '/').endsWith('/')
    ? String(routesRoot || '/')
    : `${routesRoot}/`

  const sections = ['variant-added']
  documentRef.documentElement.dispatchEvent(
    eventDetail('cart:prepare-bundled-sections', { sections }),
  )

  const { responseJson } = await requestCartAdd({
    graph,
    fetchImpl,
    routesRoot: normalizedRoot,
    sections,
    sectionsUrl: `${normalizedRoot}variants/${graph.mainVariantId}`,
    signal,
  })
  throwIfSignalAborted(signal)

  if (intent === 'add' && (cartType === 'page' || pageType === 'cart')) {
    locationRef.href = `${normalizedRoot}cart`
    return { cart: null, destination: 'cart', responseJson }
  }

  let cart
  try {
    const cartResponse = await fetchImpl(`${normalizedRoot}cart.js`, { signal })
    if (!cartResponse.ok) throw new Error('Cart refresh request failed.')
    cart = await cartResponse.json()
    if (!cart || typeof cart !== 'object') {
      throw new Error('Cart refresh response was invalid.')
    }
    throwIfSignalAborted(signal)
  } catch (error) {
    if (error?.name === 'AbortError') throw error
    throw cartRequestError('CART_REFRESH_FAILED', {
      cartMayHaveChanged: true,
      cause: error,
    })
  }

  throwIfSignalAborted(signal)
  cart.sections = responseJson.sections
  form.dispatchEvent(
    eventDetail('variant:add', {
      items: responseJson.items ?? [responseJson],
      cart,
    }),
  )
  throwIfSignalAborted(signal)
  documentRef.documentElement.dispatchEvent(
    eventDetail('cart:change', {
      baseEvent: 'variant:add',
      cart,
    }),
  )

  return { cart, destination: null, responseJson }
}

function throwIfSignalAborted(signal) {
  if (!signal?.aborted) return

  const error = new Error('The operation was aborted.', {
    cause: signal.reason,
  })
  error.name = 'AbortError'
  throw error
}

export function resolveCartSubmissionFailure(error, labels = {}) {
  if (error?.name === 'AbortError') {
    return {
      aborted: true,
      dispatchCartError: false,
      message: '',
      refreshCart: false,
      tone: null,
    }
  }

  if (error?.cartMayHaveChanged === true) {
    return {
      aborted: false,
      dispatchCartError: false,
      message: labels.addedRefreshFailed ?? labels.added ?? '',
      refreshCart: true,
      tone: 'warning',
    }
  }

  const rejectedMessage = String(error?.customerMessage ?? '').trim()
  const exposesUnpublishedVariant = /cannot\s+find\s+variant/iu.test(rejectedMessage)

  return {
    aborted: false,
    dispatchCartError: true,
    message: exposesUnpublishedVariant
      ? labels.addFailed || ''
      : rejectedMessage || labels.addFailed || '',
    refreshCart: error?.code === 'CART_ADD_REJECTED',
    tone: 'error',
  }
}

export function projectCanonicalFormState(
  graph,
  entries = [],
  { mainVariantId: canonicalMainVariantId } = {},
) {
  let mainVariantId = toId(canonicalMainVariantId) ?? graph?.mainVariantId ?? null
  let mainQuantity = graph?.mainQuantity ?? 1
  let sellingPlanId = graph?.sellingPlanId ?? null
  let hasUnsupportedLineItemProperty = false
  const lineItemProperties = {}

  for (const [name, rawValue] of entries) {
    const isScalar =
      rawValue === null ||
      rawValue === undefined ||
      ['string', 'number', 'boolean'].includes(typeof rawValue)
    const value = isScalar ? String(rawValue ?? '') : ''
    if (name === 'id' && !canonicalMainVariantId) mainVariantId = toId(value)
    else if (name === 'quantity') mainQuantity = positiveInteger(value)
    else if (name === 'selling_plan') sellingPlanId = toId(value)
    else {
      const property = /^properties\[(.+)\]$/u.exec(name)
      if (property && isScalar) lineItemProperties[property[1]] = value
      else if (property) hasUnsupportedLineItemProperty = true
    }
  }

  return freezeSelectionGraph({
    ...graph,
    mainVariantId,
    mainQuantity,
    sellingPlanId,
    lineItemProperties,
    hasUnsupportedLineItemProperty,
    revision: (graph?.revision ?? 0) + 1,
  })
}

export function computeCountdownState(endsAt, now = Date.now()) {
  const target = Date.parse(endsAt)
  const current = Number(now)
  if (!Number.isFinite(target) || !Number.isFinite(current)) {
    return {
      valid: false,
      expired: false,
      hidden: true,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    }
  }

  const remaining = Math.max(0, target - current)
  const expired = remaining === 0
  const totalSeconds = Math.floor(remaining / 1000)

  return {
    valid: true,
    expired,
    hidden: expired,
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  }
}

export class SubmissionGate {
  #inFlight = null

  get busy() {
    return this.#inFlight !== null
  }

  run(task) {
    if (this.#inFlight) return this.#inFlight

    let resolveTask
    let rejectTask
    const pending = new Promise((resolve, reject) => {
      resolveTask = resolve
      rejectTask = reject
    })
    this.#inFlight = pending.finally(() => {
      this.#inFlight = null
    })
    try {
      resolveTask(task())
    } catch (error) {
      rejectTask(error)
    }
    return this.#inFlight
  }
}

export function createLifecycleRegistry({
  AbortControllerImpl = globalThis.AbortController,
  clearTimeoutImpl = globalThis.clearTimeout,
  clearIntervalImpl = globalThis.clearInterval,
} = {}) {
  const abortController = new AbortControllerImpl()
  const observers = new Set()
  const timeouts = new Set()
  const intervals = new Set()
  const nodes = new Set()
  const disposers = new Set()
  let disposed = false

  return {
    signal: abortController.signal,
    trackObserver(observer) {
      if (observer) observers.add(observer)
      return observer
    },
    untrackObserver(observer) {
      observers.delete(observer)
      return observer
    },
    trackTimeout(timeoutId) {
      if (timeoutId !== null && timeoutId !== undefined) timeouts.add(timeoutId)
      return timeoutId
    },
    trackInterval(intervalId) {
      if (intervalId !== null && intervalId !== undefined) intervals.add(intervalId)
      return intervalId
    },
    trackNode(node) {
      if (node) nodes.add(node)
      return node
    },
    trackDisposer(disposer) {
      if (typeof disposer === 'function') disposers.add(disposer)
      return disposer
    },
    untrackDisposer(disposer) {
      disposers.delete(disposer)
      return disposer
    },
    dispose() {
      if (disposed) return
      disposed = true
      abortController.abort()
      for (const observer of observers) observer.disconnect?.()
      for (const timeoutId of timeouts) clearTimeoutImpl?.(timeoutId)
      for (const intervalId of intervals) clearIntervalImpl?.(intervalId)
      for (const node of nodes) node.remove?.()
      for (const disposer of disposers) disposer()
      observers.clear()
      timeouts.clear()
      intervals.clear()
      nodes.clear()
      disposers.clear()
    },
  }
}

function parseConfig(root) {
  const source = root.querySelector('[data-main-product-figma-config]')?.textContent ?? '{}'
  try {
    return JSON.parse(source)
  } catch (error) {
    console.error('Main product configuration could not be parsed.', error)
    return {}
  }
}

function formatMoneyNumber(cents, precision, thousandsSeparator, decimalSeparator) {
  const amount = Number(cents) / 100
  if (!Number.isFinite(amount)) return ''

  const [whole, decimal] = amount.toFixed(precision).split('.')
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/gu, thousandsSeparator)
  return decimal === undefined ? groupedWhole : `${groupedWhole}${decimalSeparator}${decimal}`
}

export function formatMoney(cents, config = {}) {
  const moneyFormat = String(config.moneyFormat || '{{amount}}')
  const placeholder = moneyFormat.match(/\{\{\s*(amount[\w_]*)\s*\}\}/u)?.[1]
  if (!placeholder) return ''

  const formats = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
  }
  const format = formats[placeholder] ?? formats.amount
  const formattedAmount = formatMoneyNumber(cents, ...format)
  if (formattedAmount === '') return ''

  return moneyFormat
    .replace(/\{\{\s*amount[\w_]*\s*\}\}/u, formattedAmount)
    .replace(/<[^>]*>/gu, '')
}

export function formatSummaryMoney(cents, config) {
  const amount = formatMoney(cents, {
    ...config,
    moneyFormat: config?.summaryMoneyFormat || config?.moneyFormat,
  })
  const currencyCode = String(config?.currencyCode || '').trim()
  return currencyCode && amount ? `${currencyCode} ${amount}` : amount
}

export function selectAddonDisplayVariant(state) {
  return (
    state?.variants?.find((variant) => toId(variant.id) === toId(state.resolvedVariantId)) ??
    state?.variants?.find(Boolean) ??
    null
  )
}

export function buildSelectionSummaryModel(graph, mainVariant, config = {}) {
  if (!mainVariant) return null

  const variantTitle = String(mainVariant.title ?? '').trim()
  const productTitle = String(config.productTitle ?? '').trim()
  const mainLabel =
    variantTitle && variantTitle !== 'Default Title' ? variantTitle : productTitle || variantTitle
  const rows = [
    {
      label: mainLabel,
      detail: formatSummaryMoney(
        resolveQuantityUnitPrice(mainVariant, graph?.mainQuantity),
        config,
      ),
      trailing:
        positiveInteger(graph?.mainQuantity) > 1 ? `×${positiveInteger(graph.mainQuantity)}` : '',
      image: mainVariant.featuredImage ?? '',
      main: true,
    },
  ]

  for (const state of selectedAddonStates(graph)) {
    const selectedVariant = selectAddonDisplayVariant(state)
    const selectedVariantTitle = String(selectedVariant?.title ?? '').trim()
    const label =
      selectedVariantTitle && selectedVariantTitle !== 'Default Title'
        ? `${state.title} · ${selectedVariantTitle}`
        : state.title
    rows.push({
      label,
      detail: '',
      trailing: `×${positiveInteger(state.quantity)}`,
      image: '',
      main: false,
    })
  }

  return {
    rows,
    total: computeSelectionTotals(graph, mainVariant).price,
  }
}

export function resolveRewardEstimate({ template, variantPrice } = {}) {
  const normalizedTemplate = String(template ?? '').trim()
  const price = Number(variantPrice)
  const placeholder = '%seeds%'
  if (!normalizedTemplate.includes(placeholder) || !Number.isFinite(price) || price < 0) {
    return ''
  }

  const seeds = Math.round(price / 200)
  return normalizedTemplate.split(placeholder).join(String(seeds))
}

function element(tagName, attributes = {}, text = null, documentRef = globalThis.document) {
  const node = documentRef.createElement(tagName)
  for (const [name, value] of Object.entries(attributes)) {
    if (value === false || value === null || value === undefined) continue
    if (value === true) node.setAttribute(name, '')
    else node.setAttribute(name, String(value))
  }
  if (text !== null && text !== undefined) node.textContent = String(text)
  return node
}

function eventDetail(name, detail) {
  return new CustomEvent(name, { bubbles: true, detail })
}

export function syncDialogScrollLock(documentRef = globalThis.document) {
  if (!documentRef?.documentElement) return false
  const hasOpenDialog = Boolean(
    documentRef.querySelector?.('main-product-figma [data-role="dialog"][open]'),
  )
  documentRef.documentElement.toggleAttribute('data-main-product-figma-dialog-open', hasOpenDialog)
  return hasOpenDialog
}

function registerBrowserElement() {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    typeof window.HTMLElement === 'undefined'
  ) {
    return
  }

  class MainProductFigmaElement extends window.HTMLElement {
    #config = {}
    #dialogTrigger = null
    #fixedBarRebindLifecycle = null
    #fixedObserver = null
    #galleryControlsAnchor = null
    #galleryControlsNode = null
    #galleryGroup = 'features'
    #galleryNativeFilters = createGalleryNativeFilterOwner()
    #galleryMotionPreference = null
    #gallerySettleAutoplayRestore = null
    #galleryThumbnailWindowStartIndex = 0
    #galleryThumbnailsExpanded = false
    #galleryTouchStart = null
    #graph = null
    #interactionLocks = new Set()
    #lifecycle = null
    #mediaRailStickyDisposer = null
    #mediaRailStickyScheduler = null
    #packageVariantViewport = null
    #submissionGate = new SubmissionGate()
    #preservedGalleryPosition = null
    #variantThumbnailPinnedPosition = null
    #variantFailed = false
    #variantPending = false
    #variantPendingTimer = null

    connectedCallback() {
      this.#lifecycle?.dispose()
      const lifecycle = createLifecycleRegistry()
      this.#lifecycle = lifecycle
      queueMicrotask(() => {
        if (this.#lifecycle !== lifecycle) return
        this.#initialize(lifecycle)
      })
    }

    disconnectedCallback() {
      this.#closeDialog(false)
      this.#unbindMediaRailSticky()
      this.#unbindFixedBarVisibility()
      this.#fixedBarRebindLifecycle = null
      if (this.#variantPendingTimer !== null) window.clearTimeout(this.#variantPendingTimer)
      this.#variantPendingTimer = null
      this.#packageVariantViewport = null
      this.#preservedGalleryPosition = null
      this.#galleryThumbnailWindowStartIndex = 0
      this.#galleryTouchStart = null
      this.#gallerySettleAutoplayRestore?.()
      this.#gallerySettleAutoplayRestore = null
      restoreGalleryMotionPreference(this.querySelector('product-gallery'))
      this.#galleryMotionPreference = null
      this.#variantThumbnailPinnedPosition = null
      this.#clearVariantFailure()
      this.#interactionLocks.clear()
      this.#applyInteractionLock()
      this.#variantPending = false
      this.removeAttribute('data-variant-pending')
      this.removeAttribute('data-busy')
      this.#restoreGalleryPresentation()
      this.#lifecycle?.dispose()
      this.#lifecycle = null
    }

    #initialize(lifecycle) {
      if (!this.isConnected || this.#lifecycle !== lifecycle) return
      this.#config = parseConfig(this)
      this.#graph = this.#createGraphForMainVariant(this.#config.currentVariantId)
      this.#bindEvents()
      this.#configureGallery()
      this.#bindMediaRailSticky()
      this.#bindFixedBar()
      this.#render()
    }

    #bindEvents() {
      const signal = this.#lifecycle.signal
      const productForm = this.#form()
      this.#galleryMotionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)')
      const galleryMotionPreference = this.#galleryMotionPreference
      const syncGalleryMotionPreference = () => this.#syncGalleryMotionPreference()
      if (typeof galleryMotionPreference?.addEventListener === 'function') {
        galleryMotionPreference.addEventListener('change', syncGalleryMotionPreference)
        this.#lifecycle.trackDisposer(() =>
          galleryMotionPreference.removeEventListener?.('change', syncGalleryMotionPreference),
        )
      } else if (typeof galleryMotionPreference?.addListener === 'function') {
        galleryMotionPreference.addListener(syncGalleryMotionPreference)
        this.#lifecycle.trackDisposer(() =>
          galleryMotionPreference.removeListener?.(syncGalleryMotionPreference),
        )
      }
      this.addEventListener(
        'pointerdown',
        (event) => {
          if (event.button !== undefined && event.button !== 0) return
          this.#rememberPackageVariantViewport(
            capturePackageVariantPointerViewport({
              documentRef: document,
              packageOptionPosition: this.#config.packageOptionPosition,
              root: this,
              target: event.target,
              windowRef: window,
            }),
            { replace: true },
          )
        },
        { capture: true, signal },
      )
      this.addEventListener('change', (event) => this.#onChange(event), {
        signal,
      })
      this.addEventListener('submit', (event) => this.#onSubmit(event), {
        capture: true,
        signal,
      })
      this.addEventListener(
        'product:rerender',
        (event) => {
          const lifecycle = this.#lifecycle
          syncPdpBadgeBlocksFromRerender(this, event.detail?.htmlFragment)
          const gallery = this.querySelector('product-gallery')
          if (gallery) {
            this.#galleryNativeFilters.refreshFromRerender(gallery, event.detail?.htmlFragment)
          }
          queueMicrotask(() => {
            if (!this.isConnected || this.#lifecycle !== lifecycle) return
            this.#refreshAfterRerender()
          })
        },
        { capture: true, signal },
      )
      productForm?.addEventListener?.(
        'product:rerender',
        () => {
          const lifecycle = this.#lifecycle
          const focusedPackageVariantViewport = capturePackageVariantViewport({
            documentRef: document,
            packageOptionPosition: this.#config.packageOptionPosition,
            root: this,
            windowRef: window,
          })
          const packageVariantViewport =
            this.#packageVariantViewport ?? focusedPackageVariantViewport
          this.#packageVariantViewport = null
          if (!packageVariantViewport) return

          queueMicrotask(() => {
            if (!this.isConnected || this.#lifecycle !== lifecycle) return
            restorePackageVariantViewport({
              documentRef: document,
              root: this,
              token: packageVariantViewport,
              windowRef: window,
            })
            lifecycle?.trackTimeout(
              window.setTimeout(() => {
                if (!this.isConnected || this.#lifecycle !== lifecycle) return
                restoreViewportScroll(window, packageVariantViewport.scroll)
              }, 0),
            )
          })
        },
        { capture: true, signal },
      )
      this.addEventListener(
        'variant:change',
        (event) => {
          if (this.#confirmMainVariant(event.detail?.variant?.id)) {
            this.#selectVariantFeaturedMedia()
          }
        },
        { signal },
      )
      this.addEventListener(
        'cart:error',
        (event) => {
          if (event.target !== this.#form()) return
          if (!event.detail || typeof event.detail !== 'object') return
          const failure = resolveCartSubmissionFailure(
            { customerMessage: event.detail.error },
            this.#config.labels,
          )
          event.detail.error = failure.message
          this.#setStatus(failure.message, failure.tone)
        },
        { capture: true, signal },
      )
      for (const eventName of ['carousel:select', 'carousel:change']) {
        this.addEventListener(
          eventName,
          (event) => {
            const carousel = event.target
            const gallery = carousel?.closest?.('product-gallery')
            if (!carousel?.matches?.('media-carousel') || !gallery || !this.contains(gallery)) {
              return
            }

            const requestedPosition = Number.parseInt(event.detail?.index, 10) + 1
            const state = this.#syncGalleryNavigation(gallery, requestedPosition)
            this.#configureGalleryThumbnails(gallery)
            if (state.currentPosition !== null && state.currentPosition !== requestedPosition) {
              this.#selectGalleryPosition(carousel, state.currentPosition)
            }
          },
          { signal },
        )
      }
      this.addEventListener(
        'carousel:settle',
        (event) => {
          this.#gallerySettleAutoplayRestore?.()
          const restore = suppressGallerySettleAutoplay({
            event,
            reducedMotion: this.#galleryMotionPreference?.matches === true,
            root: this,
          })
          this.#gallerySettleAutoplayRestore = restore
          if (!restore) return
          queueMicrotask(() => {
            if (this.#gallerySettleAutoplayRestore !== restore) return
            restore()
            this.#gallerySettleAutoplayRestore = null
          })
        },
        { capture: true, signal },
      )
      this.addEventListener(
        'touchstart',
        (event) => {
          const carousel = event.target.closest?.('media-carousel')
          const touch = event.touches?.[0]
          if (event.touches?.length !== 1 || !carousel || !this.contains(carousel) || !touch) {
            this.#galleryTouchStart = null
            return
          }
          const gallery = carousel.closest?.('product-gallery')
          const mediaItems = [...(gallery?.querySelectorAll('.product-gallery__media') ?? [])]
          const visiblePositions = mediaItems.flatMap((item, index) =>
            item.hidden ? [] : [index + 1],
          )
          this.#galleryTouchStart = {
            boundaryOwned: false,
            boundaryRejected: false,
            carousel,
            identifier: touch.identifier,
            position: this.#currentGalleryPosition(carousel, mediaItems),
            visiblePositions,
            x: touch.clientX,
            y: touch.clientY,
          }
        },
        { passive: true, signal },
      )
      this.addEventListener(
        'touchmove',
        (event) => {
          const gesture = this.#galleryTouchStart
          const touch = [...(event.touches ?? [])].find(
            (candidate) => candidate.identifier === gesture?.identifier,
          )
          if (
            !gesture ||
            event.touches?.length !== 1 ||
            !touch ||
            !gesture.carousel ||
            !this.contains(gesture.carousel)
          ) {
            return
          }

          if (gesture.boundaryRejected === true) return

          const intent =
            gesture.boundaryOwned === true
              ? 'boundary'
              : resolveGalleryBoundarySwipeIntent({
                  currentPosition: gesture.position,
                  endX: touch.clientX,
                  endY: touch.clientY,
                  startX: gesture.x,
                  startY: gesture.y,
                  visiblePositions: gesture.visiblePositions,
                })
          if (intent === 'pending') return
          if (intent === 'native') {
            gesture.boundaryRejected = true
            return
          }
          if (!event.cancelable) {
            gesture.boundaryRejected = true
            return
          }

          event.preventDefault()
          if (event.defaultPrevented) {
            gesture.boundaryOwned = true
          } else {
            gesture.boundaryRejected = true
          }
        },
        { passive: false, signal },
      )
      this.addEventListener(
        'touchend',
        (event) => {
          const gesture = this.#galleryTouchStart
          this.#galleryTouchStart = null
          const touch = [...(event.changedTouches ?? [])].find(
            (candidate) => candidate.identifier === gesture?.identifier,
          )
          const carousel = gesture?.carousel
          const gallery = carousel?.closest?.('product-gallery')
          if (
            !touch ||
            gesture?.boundaryOwned !== true ||
            event.touches?.length > 0 ||
            !carousel ||
            !gallery ||
            !this.contains(gallery)
          ) {
            return
          }
          const targetPosition =
            resolveGallerySwipeWrapTarget({
              currentPosition: gesture.position,
              endX: touch.clientX,
              endY: touch.clientY,
              startX: gesture.x,
              startY: gesture.y,
              visiblePositions: gesture.visiblePositions,
            }) ?? gesture.position
          if (!Number.isFinite(targetPosition)) return

          this.#selectGalleryPosition(carousel, targetPosition, {
            animate: resolveGallerySelectionAnimation({
              currentPosition: gesture.position,
              reducedMotion: this.#galleryMotionPreference?.matches === true,
              targetPosition,
              visiblePositions: gesture.visiblePositions,
            }),
          })
          this.#syncGalleryNavigation(gallery, targetPosition)
          this.#configureGalleryThumbnails(gallery)
        },
        { passive: true, signal },
      )
      this.addEventListener(
        'touchcancel',
        () => {
          this.#galleryTouchStart = null
        },
        { passive: true, signal },
      )
      this.addEventListener(
        'click',
        (event) => {
          const previewControl = event.target.closest?.(
            '[data-main-product-figma-variant-thumbnail-preview]',
          )
          if (!previewControl || previewControl.hidden) return
          const gallery = previewControl.closest('product-gallery')
          const carousel = gallery?.querySelector('media-carousel')
          const targetPosition = Number.parseInt(
            previewControl.getAttribute('data-main-product-figma-variant-thumbnail-target'),
            10,
          )
          if (!gallery || !carousel || !Number.isFinite(targetPosition)) return

          event.preventDefault()
          event.stopPropagation()
          this.#selectGalleryPosition(carousel, targetPosition)
          this.#syncGalleryNavigation(gallery, targetPosition)
        },
        { capture: true, signal },
      )
      this.addEventListener(
        'click',
        (event) => {
          const thumbnail = event.target.closest?.('.product-gallery__thumbnail')
          if (!thumbnail || thumbnail.hidden || thumbnail.hasAttribute('inert')) return
          const thumbnailList = thumbnail.closest?.('.product-gallery__thumbnail-list')
          const gallery = thumbnailList?.closest?.('product-gallery')
          const carousel = gallery?.querySelector('media-carousel')
          if (!thumbnailList || !gallery || !carousel || !this.contains(gallery)) return

          const thumbnails = [
            ...thumbnailList.querySelectorAll(':scope > .product-gallery__thumbnail'),
          ]
          const targetPosition = resolveGalleryThumbnailPosition(thumbnails, thumbnail)
          const mediaItems = [...gallery.querySelectorAll('.product-gallery__media')]
          const visiblePositions = mediaItems.flatMap((item, index) =>
            item.hidden ? [] : [index + 1],
          )
          if (targetPosition === null || !visiblePositions.includes(targetPosition)) return

          const currentPosition = this.#currentGalleryPosition(carousel, mediaItems)
          event.preventDefault()
          event.stopPropagation()
          this.#selectGalleryPosition(carousel, targetPosition, {
            animate: resolveGallerySelectionAnimation({
              currentPosition,
              reducedMotion: this.#galleryMotionPreference?.matches === true,
              targetPosition,
              visiblePositions,
            }),
          })
          this.#syncGalleryNavigation(gallery, targetPosition)
          this.#configureGalleryThumbnails(gallery)
        },
        { capture: true, signal },
      )
      this.addEventListener(
        'click',
        (event) => {
          const pointerCarousel = event.target.closest?.('media-carousel')
          if (
            !pointerCarousel ||
            !this.contains(pointerCarousel) ||
            event.target.closest?.('button, a[href]') ||
            window.matchMedia?.('screen and (pointer: fine)')?.matches !== true
          ) {
            return
          }

          const gallery = pointerCarousel.closest?.('product-gallery')
          const clickedSlide = event.target.closest?.('.product-gallery__media')
          const selectedSlide = pointerCarousel.selectedSlide
          if (
            !gallery ||
            !clickedSlide ||
            clickedSlide.closest?.('media-carousel') !== pointerCarousel ||
            clickedSlide.getAttribute('data-media-type') !== 'image' ||
            selectedSlide?.getAttribute('data-media-type') !== 'image'
          ) {
            return
          }

          const mediaItems = [...gallery.querySelectorAll('.product-gallery__media')]
          const visiblePositions = mediaItems.flatMap((item, index) =>
            item.hidden ? [] : [index + 1],
          )
          const rect = pointerCarousel.getBoundingClientRect()
          const currentPosition = this.#currentGalleryPosition(pointerCarousel, mediaItems)
          const targetPosition = resolveGalleryPointerWrapTarget({
            clientX: event.clientX,
            currentPosition,
            left: rect.left,
            visiblePositions,
            width: pointerCarousel.clientWidth,
          })
          if (targetPosition === null) return

          pointerCarousel.focus({ preventScroll: true })
          event.preventDefault()
          event.stopPropagation()
          this.#selectGalleryPosition(pointerCarousel, targetPosition, {
            animate: resolveGallerySelectionAnimation({
              currentPosition,
              reducedMotion: this.#galleryMotionPreference?.matches === true,
              targetPosition,
              visiblePositions,
            }),
          })
          this.#syncGalleryNavigation(gallery, targetPosition)
          this.#configureGalleryThumbnails(gallery)
        },
        { capture: true, signal },
      )
      this.addEventListener(
        'click',
        (event) => {
          const galleryPrevious = event.target.closest?.(
            '[data-main-product-figma-gallery-previous]',
          )
          const galleryNext = event.target.closest?.('[data-main-product-figma-gallery-next]')
          if (galleryPrevious || galleryNext) {
            const gallery = this.querySelector('product-gallery')
            const carousel = gallery?.querySelector('media-carousel')
            const mediaItems = [...(gallery?.querySelectorAll('.product-gallery__media') ?? [])]
            const visiblePositions = mediaItems.flatMap((item, index) =>
              item.hidden ? [] : [index + 1],
            )
            const previewControl = gallery?.querySelector(
              '[data-main-product-figma-variant-thumbnail-preview]',
            )
            const state = resolveGalleryPresentedNavigationState(
              visiblePositions,
              this.#currentGalleryPosition(carousel, mediaItems),
              {
                previewTargetPosition: previewControl?.getAttribute(
                  'data-main-product-figma-variant-thumbnail-target',
                ),
                previewVisible: Boolean(
                  previewControl &&
                  !previewControl.hidden &&
                  previewControl.getClientRects().length,
                ),
              },
            )
            const targetPosition = galleryPrevious ? state.previousPosition : state.nextPosition
            if (!gallery || !carousel || targetPosition === null) return

            event.preventDefault()
            event.stopPropagation()
            this.#selectGalleryPosition(carousel, targetPosition, {
              animate: resolveGallerySelectionAnimation({
                currentPosition: state.currentPosition,
                reducedMotion: this.#galleryMotionPreference?.matches === true,
                targetPosition,
                visiblePositions,
              }),
            })
            this.#syncGalleryNavigation(gallery, targetPosition)
            this.#configureGalleryThumbnails(gallery)
            return
          }

          const thumbnailPrevious = event.target.closest?.(
            '[data-main-product-figma-gallery-thumbnail-previous]',
          )
          const thumbnailNext = event.target.closest?.(
            '[data-main-product-figma-gallery-thumbnail-next]',
          )
          if (thumbnailPrevious || thumbnailNext) {
            event.preventDefault()
            this.#navigateGalleryThumbnail(thumbnailPrevious ? 'previous' : 'next')
            return
          }

          const thumbnailToggle = event.target.closest?.(
            '[data-main-product-figma-gallery-thumbnail-toggle]',
          )
          if (thumbnailToggle) {
            event.preventDefault()
            this.#navigateGalleryThumbnail('next')
            return
          }

          const galleryFilter = event.target.closest?.('[data-main-product-figma-gallery-filter]')
          if (galleryFilter) {
            event.preventDefault()
            this.#galleryThumbnailsExpanded = false
            this.#galleryThumbnailWindowStartIndex = 0
            this.#variantThumbnailPinnedPosition = null
            this.#galleryGroup =
              galleryFilter.getAttribute('data-main-product-figma-gallery-filter') ?? 'features'
            this.#configureGallery()
            this.#syncGalleryVideoPlayback()
            return
          }

          const decrease = event.target.closest?.('[data-main-product-figma-addon-decrease]')
          const increase = event.target.closest?.('[data-main-product-figma-addon-increase]')
          const quantityAction = decrease ?? increase
          if (quantityAction) {
            event.preventDefault()
            const attribute = decrease
              ? 'data-main-product-figma-addon-decrease'
              : 'data-main-product-figma-addon-increase'
            const productId = quantityAction.getAttribute(attribute)
            const state = productId ? this.#graph?.addonState?.[productId] : null
            if (state) {
              const selectedVariant = selectAddonDisplayVariant(state)
              const increment = resolveVariantQuantityRule(selectedVariant).increment
              this.#graph = setAddonQuantity(
                this.#graph,
                productId,
                state.quantity + (decrease ? -increment : increment),
              )
              this.#render()
            }
            return
          }

          const dialogTrigger = event.target.closest?.('[data-main-product-figma-dialog-trigger]')
          if (dialogTrigger) {
            event.preventDefault()
            this.#openDialog(dialogTrigger)
            return
          }

          const dialog = event.target.closest?.('[data-role="dialog"]')
          if (event.target.closest?.('[data-role="dialog-close"]') || event.target === dialog) {
            this.#closeDialog()
            return
          }

          const retry = event.target.closest?.('[data-main-product-figma-retry]')
          if (retry) this.#setStatus('')
        },
        { signal },
      )
      this.addEventListener(
        'keydown',
        (event) => {
          const carousel = event.target?.matches?.('media-carousel') ? event.target : null
          if (carousel && this.contains(carousel)) {
            if (
              event.defaultPrevented ||
              event.isComposing ||
              event.altKey ||
              event.ctrlKey ||
              event.metaKey
            ) {
              return
            }

            const gallery = carousel.closest?.('product-gallery')
            const mediaItems = [...(gallery?.querySelectorAll('.product-gallery__media') ?? [])]
            const visiblePositions = mediaItems.flatMap((item, index) =>
              item.hidden ? [] : [index + 1],
            )
            const currentPosition = this.#currentGalleryPosition(carousel, mediaItems)
            const targetPosition = resolveGalleryKeyboardWrapTarget({
              currentPosition,
              direction: window.getComputedStyle?.(carousel)?.direction,
              key: event.key,
              visiblePositions,
            })
            if (!gallery || targetPosition === null) return

            event.preventDefault()
            event.stopPropagation()
            this.#selectGalleryPosition(carousel, targetPosition, {
              animate: resolveGallerySelectionAnimation({
                currentPosition,
                reducedMotion: this.#galleryMotionPreference?.matches === true,
                targetPosition,
                visiblePositions,
              }),
            })
            this.#syncGalleryNavigation(gallery, targetPosition)
            this.#configureGalleryThumbnails(gallery)
            return
          }

          const galleryFilter = event.target.closest?.('[data-main-product-figma-gallery-filter]')
          if (!galleryFilter) return

          const controls = galleryFilter.closest('[data-main-product-figma-gallery-controls]')
          const tabs = [
            ...(controls?.querySelectorAll(
              '[data-main-product-figma-gallery-filter]:not([hidden])',
            ) ?? []),
          ]
          const targetIndex = galleryTabIndexForKey(
            tabs.indexOf(galleryFilter),
            tabs.length,
            event.key,
            window.getComputedStyle?.(controls).direction,
          )
          if (targetIndex === null) return

          event.preventDefault()
          const target = tabs[targetIndex]
          target?.focus()
          this.#galleryThumbnailsExpanded = false
          this.#galleryThumbnailWindowStartIndex = 0
          this.#variantThumbnailPinnedPosition = null
          this.#galleryGroup =
            target?.getAttribute('data-main-product-figma-gallery-filter') ?? this.#galleryGroup
          this.#configureGallery()
          this.#syncGalleryVideoPlayback()
        },
        { signal },
      )

      const dialog = this.querySelector('[data-role="dialog"]')
      dialog?.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault()
          this.#closeDialog()
        },
        { signal },
      )

      document.addEventListener(
        'shopify:section:load',
        (event) => {
          if (event.target?.contains?.(this)) {
            this.#refreshAfterRerender()
          } else if (this.#isFooterSectionLifecycleTarget(event.target)) {
            this.#queueFixedBarRebind()
          }
        },
        { signal },
      )
      document.addEventListener(
        'shopify:section:unload',
        (event) => {
          if (event.target?.contains?.(this)) {
            this.disconnectedCallback()
          } else if (this.#isFooterSectionLifecycleTarget(event.target)) {
            this.#queueFixedBarRebind()
          }
        },
        { signal },
      )
    }

    #isFooterSectionLifecycleTarget(target) {
      return (
        target?.matches?.('.shopify-section--footer, .footer') === true ||
        Boolean(target?.querySelector?.('.footer'))
      )
    }

    #queueFixedBarRebind() {
      const lifecycle = this.#lifecycle
      if (!lifecycle || this.#fixedBarRebindLifecycle === lifecycle) return

      this.#fixedBarRebindLifecycle = lifecycle
      queueMicrotask(() => {
        if (this.#fixedBarRebindLifecycle !== lifecycle) return
        this.#fixedBarRebindLifecycle = null
        if (!this.isConnected || this.#lifecycle !== lifecycle) return
        this.#bindFixedBar()
      })
    }

    #unbindFixedBarVisibility() {
      if (this.#fixedObserver) {
        this.#fixedObserver.disconnect()
        this.#lifecycle?.untrackObserver(this.#fixedObserver)
        this.#fixedObserver = null
      }
      const fixedBar = this.querySelector('[data-role="fixed-bar"]')
      applyFixedBarVisibilityState(fixedBar, false)
    }

    #bindFixedBar() {
      const purchaseActions = this.querySelector('[data-role="purchase-actions"]')
      const fixedBar = this.querySelector('[data-role="fixed-bar"]')
      const footer = document.querySelector('.footer')
      this.#unbindFixedBarVisibility()
      if (!purchaseActions || !fixedBar || typeof IntersectionObserver === 'undefined') return

      const observer = createFixedBarVisibilityObserver({
        fixedBar,
        footer,
        mainActions: purchaseActions,
        onVisibilityChange: () => this.#mediaRailStickyScheduler?.schedule(),
      })
      if (!observer) return
      this.#fixedObserver = observer
      this.#lifecycle.trackObserver(observer)
    }

    #unbindMediaRailSticky() {
      if (this.#mediaRailStickyDisposer) {
        this.#lifecycle?.untrackDisposer(this.#mediaRailStickyDisposer)
        this.#mediaRailStickyDisposer = null
      }
      this.#mediaRailStickyScheduler?.dispose()
      this.#mediaRailStickyScheduler = null
      applyMediaRailLifecycleTeardown({
        fixedBar: this.querySelector('[data-role="fixed-bar"]'),
        infoRail: this.querySelector('[data-role="info-rail"]'),
        mediaRail: this.querySelector('[data-role="media-rail"]'),
      })
    }

    #bindMediaRailSticky() {
      this.#unbindMediaRailSticky()
      if (!this.#lifecycle) return

      const lifecycle = this.#lifecycle
      const scheduler = createMediaRailStickyScheduler({
        MutationObserverImpl: typeof MutationObserver === 'undefined' ? null : MutationObserver,
        ResizeObserverImpl: typeof ResizeObserver === 'undefined' ? null : ResizeObserver,
        documentRef: document,
        getResizeTargets: () => [
          this.querySelector('[data-role="media-rail"]'),
          this.querySelector('.product-gallery__thumbnail-list-wrapper'),
          this.querySelector('[data-role="info-rail"]'),
          this.querySelector('[data-role="fixed-bar"]'),
          document.querySelector('.shopify-section--header'),
          document.querySelector('.shopify-section--announcement-bar'),
          document.querySelector('#PBarNextFrameWrapper'),
        ],
        syncResponsivePresentation: () => {
          const gallery = this.querySelector('product-gallery')
          if (gallery) {
            this.#configureGalleryThumbnails(gallery, { followCurrent: false })
            const carousel = gallery.querySelector('media-carousel')
            const mediaItems = [...gallery.querySelectorAll('.product-gallery__media')]
            this.#syncGalleryNavigation(gallery, this.#currentGalleryPosition(carousel, mediaItems))
          }
        },
        syncStickyGeometry: () => this.#syncMediaRailStickyOffset(),
        windowRef: window,
      })
      this.#mediaRailStickyScheduler = scheduler
      const disposeScheduler = () => {
        scheduler.dispose()
        if (this.#mediaRailStickyDisposer === disposeScheduler) {
          this.#mediaRailStickyDisposer = null
        }
        if (this.#mediaRailStickyScheduler !== scheduler) return
        this.#mediaRailStickyScheduler = null
        applyMediaRailLifecycleTeardown({
          fixedBar: this.querySelector('[data-role="fixed-bar"]'),
          infoRail: this.querySelector('[data-role="info-rail"]'),
          mediaRail: this.querySelector('[data-role="media-rail"]'),
        })
      }
      this.#mediaRailStickyDisposer = lifecycle.trackDisposer(disposeScheduler)
    }

    #syncMediaRailStickyOffset() {
      const mediaRail = this.querySelector('[data-role="media-rail"]')
      const infoRail = this.querySelector('[data-role="info-rail"]')
      const fixedBar = this.querySelector('[data-role="fixed-bar"]')
      const previewBarBounds = document
        .querySelector('#PBarNextFrameWrapper')
        ?.getBoundingClientRect()
      const previewBarObstructionHeight = resolveViewportBottomOverlap({
        viewportHeight: window.innerHeight,
        obstructionTop: previewBarBounds?.top,
        obstructionBottom: previewBarBounds?.bottom,
      })
      applyFixedBarViewportBottomOffset(fixedBar, previewBarObstructionHeight)

      if (window.innerWidth < 1024) {
        mediaRail?.style.removeProperty('--mpf-media-sticky-top')
        infoRail?.style.removeProperty('--mpf-info-sticky-top')
        return
      }

      const computedStyle = window.getComputedStyle(this)
      const fixedBarVisible = fixedBar?.hasAttribute('data-visible') === true
      const obstruction = resolveMediaStickyObstructionState({
        announcementHeight: computedStyle.getPropertyValue('--announcement-bar-height'),
        fixedBarHeight: fixedBarVisible ? fixedBar.getBoundingClientRect().height : 0,
        fixedBarVisible,
        headerHeight: computedStyle.getPropertyValue('--header-height'),
        headerVisibility: computedStyle.getPropertyValue('--header-is-visible'),
        stickyAnnouncementEnabled: computedStyle.getPropertyValue(
          '--sticky-announcement-bar-enabled',
        ),
        stickyHeaderEnabled: computedStyle.getPropertyValue('--sticky-header-enabled'),
      })
      const defaultTop = obstruction.topObstructionHeight + 24
      const bottomGap = 24
      const bottomObstructionHeight =
        obstruction.bottomObstructionHeight + previewBarObstructionHeight
      const syncRail = (rail, property, enabled = true) => {
        if (!rail || !enabled) {
          rail?.style.removeProperty(property)
          return
        }
        const stickyTop = resolveMediaStickyTop({
          viewportHeight: window.innerHeight,
          railHeight: rail.getBoundingClientRect().height,
          defaultTop,
          bottomGap,
          obstructionHeight: bottomObstructionHeight,
        })
        rail.style.setProperty(property, `${Math.round(stickyTop)}px`)
      }
      syncRail(
        mediaRail,
        '--mpf-media-sticky-top',
        this.getAttribute('data-sticky-media') === 'true',
      )
      syncRail(infoRail, '--mpf-info-sticky-top')
    }

    #restoreGalleryPresentation() {
      this.#galleryGroup = 'features'
      this.#galleryThumbnailWindowStartIndex = 0
      this.#galleryThumbnailsExpanded = false
      this.#variantThumbnailPinnedPosition = null
      const gallery = this.querySelector('product-gallery')
      if (!gallery) return

      this.#galleryNativeFilters.capture(gallery)
      const nativeFilteredPositions = this.#galleryNativeFilters.get(gallery)
      if (nativeFilteredPositions !== null) {
        const mediaItems = [...gallery.querySelectorAll('.product-gallery__media')]
        const actualPosition = this.#currentGalleryPosition(
          gallery.querySelector('media-carousel'),
          mediaItems,
        )
        restoreGalleryPresentation({
          gallery,
          controls:
            this.querySelector('[data-main-product-figma-gallery-controls]') ??
            this.#galleryControlsNode,
          navigation: this.querySelector('[data-role="gallery-navigation"]'),
          toggle: this.querySelector('[data-main-product-figma-gallery-thumbnail-toggle]'),
          thumbnailPrevious: this.querySelector(
            '[data-main-product-figma-gallery-thumbnail-previous]',
          ),
          thumbnailNext: this.querySelector('[data-main-product-figma-gallery-thumbnail-next]'),
          previewControl: this.querySelector('[data-main-product-figma-variant-thumbnail-preview]'),
          nativeFilteredPositions,
          actualPosition,
          restoreVariantThumbnailAccessibility: () =>
            this.#restoreVariantThumbnailAccessibility(gallery),
        })
      }
      this.#galleryNativeFilters.release(gallery)
    }

    #mountGalleryControls(gallery, controls) {
      if (!gallery || !controls) return

      if (this.#galleryControlsNode !== controls) {
        this.#galleryControlsAnchor?.remove()
        const anchor = document.createComment('main-product-figma-gallery-controls')
        controls.parentNode?.insertBefore(anchor, controls)
        this.#galleryControlsNode = controls
        this.#galleryControlsAnchor = anchor
        this.#lifecycle?.trackDisposer(() => {
          if (this.#galleryControlsNode !== controls) return
          if (this.contains(anchor) && anchor.parentNode) {
            anchor.parentNode.insertBefore(controls, anchor.nextSibling)
          }
          anchor.remove()
          this.#galleryControlsAnchor = null
          this.#galleryControlsNode = null
        })
      }

      if (controls.parentElement !== gallery) gallery.append(controls)
    }

    #configureGallery() {
      const gallery = this.querySelector('product-gallery')
      const controls =
        this.querySelector('[data-main-product-figma-gallery-controls]') ??
        this.#galleryControlsNode
      const navigation = this.querySelector('[data-role="gallery-navigation"]')
      controls?.setAttribute('hidden', '')
      navigation?.setAttribute('hidden', '')
      this.querySelector('[data-main-product-figma-gallery-thumbnail-toggle]')?.setAttribute(
        'hidden',
        '',
      )
      this.querySelector('[data-main-product-figma-gallery-thumbnail-previous]')?.setAttribute(
        'hidden',
        '',
      )
      this.querySelector('[data-main-product-figma-gallery-thumbnail-next]')?.setAttribute(
        'hidden',
        '',
      )
      if (!gallery) {
        return
      }
      this.#syncGalleryMotionPreference(gallery)
      this.#mountGalleryControls(gallery, controls)

      const mediaItems = [...gallery.querySelectorAll('.product-gallery__media')]
      this.#galleryNativeFilters.capture(gallery)
      const impactFilteredIndexes = this.#galleryNativeFilters.get(gallery)
      if (impactFilteredIndexes === null) return
      if (mediaItems.length === 0) {
        this.#clearGalleryTabState(gallery, controls)
        return
      }
      const featuredResolution = this.#resolveVariantFeaturedPreview(mediaItems)
      const featuredPosition = Number.parseInt(featuredResolution?.targetPosition, 10)
      const hasFeaturedPosition =
        Number.isFinite(featuredPosition) &&
        featuredPosition > 0 &&
        featuredPosition <= mediaItems.length
      const effectiveImpactFilteredIndexes = hasFeaturedPosition
        ? impactFilteredIndexes.filter((position) => position !== featuredPosition)
        : impactFilteredIndexes
      const impactHidden = new Set(
        effectiveImpactFilteredIndexes
          .map((index) => Number.parseInt(index, 10))
          .filter((index) => Number.isFinite(index) && index > 0),
      )
      const mediaDescriptors = mediaItems.map((item) => ({
        mediaType: item.getAttribute('data-media-type'),
        alt: item.querySelector('[alt]')?.getAttribute('alt') ?? '',
      }))
      const groupedMedia = mediaDescriptors.map((media, index) => ({
        group: galleryGroupForMedia(media),
        index,
        position: index + 1,
      }))
      const enhancementState = resolveGalleryEnhancementState(
        mediaDescriptors,
        effectiveImpactFilteredIndexes,
      )
      const { availableGroups } = enhancementState

      if (this.getAttribute('data-gallery-filter-mode') === 'off' || !enhancementState.showTabs) {
        controls?.setAttribute('hidden', '')
        this.#clearGalleryTabState(gallery, controls)
        this.#applyGalleryFilter(gallery, mediaItems, impactHidden)
        return
      }

      if (!availableGroups.includes(this.#galleryGroup)) {
        this.#galleryGroup = availableGroups[0]
      }

      const carousel = gallery.querySelector('media-carousel')
      carousel?.setAttribute('role', 'tabpanel')
      for (const button of controls?.querySelectorAll('[data-main-product-figma-gallery-filter]') ??
        []) {
        const group = button.getAttribute('data-main-product-figma-gallery-filter')
        const available = availableGroups.includes(group)
        const selected = group === this.#galleryGroup
        button.toggleAttribute('hidden', !available)
        button.setAttribute('aria-selected', String(selected))
        button.tabIndex = selected ? 0 : -1
        if (selected && button.id) carousel?.setAttribute('aria-labelledby', button.id)
      }

      const filtered = resolveGalleryFilteredPositions({
        activeGroup: this.#galleryGroup,
        groupedMedia,
        impactHiddenPositions: impactHidden,
      })
      this.#applyGalleryFilter(gallery, mediaItems, filtered)
      controls?.removeAttribute('hidden')
    }

    #syncGalleryVideoPlayback() {
      return syncGalleryVideoPlayback({
        activeGroup: this.#galleryGroup,
        gallery: this.querySelector('product-gallery'),
        reducedMotion: this.#galleryMotionPreference?.matches === true,
      })
    }

    #syncGalleryMotionPreference(gallery = this.querySelector('product-gallery')) {
      return applyGalleryMotionPreference(gallery, this.#galleryMotionPreference?.matches === true)
    }

    #clearGalleryTabState(gallery, controls) {
      const carousel = gallery.querySelector('media-carousel')
      carousel?.removeAttribute('role')
      carousel?.removeAttribute('aria-labelledby')
      for (const button of controls?.querySelectorAll('[data-main-product-figma-gallery-filter]') ??
        []) {
        button.setAttribute('aria-selected', 'false')
        button.tabIndex = -1
      }
    }

    #currentGalleryPosition(carousel, mediaItems) {
      const selectedIndex = Number.parseInt(carousel?.selectedIndex, 10)
      if (
        Number.isFinite(selectedIndex) &&
        selectedIndex >= 0 &&
        selectedIndex < mediaItems.length
      ) {
        return selectedIndex + 1
      }

      const selectedFromClass = mediaItems.findIndex((item) =>
        item.classList.contains('is-selected'),
      )
      if (selectedFromClass >= 0) return selectedFromClass + 1

      const initialIndex = Number.parseInt(carousel?.getAttribute('initial-index'), 10)
      return Number.isFinite(initialIndex) && initialIndex >= 0 ? initialIndex + 1 : 1
    }

    #selectVariantFeaturedMedia() {
      const gallery = this.querySelector('product-gallery')
      const carousel = gallery?.querySelector('media-carousel')
      const mediaItems = [...(gallery?.querySelectorAll('.product-gallery__media') ?? [])]
      const resolution = this.#resolveVariantFeaturedPreview(mediaItems)
      const targetPosition = resolution?.targetPosition
      if (!gallery || !carousel || !Number.isFinite(targetPosition)) {
        this.#preservedGalleryPosition = null
        this.#variantThumbnailPinnedPosition = null
        return false
      }

      const targetMedia = mediaItems[targetPosition - 1]
      if (!targetMedia) {
        this.#preservedGalleryPosition = null
        this.#variantThumbnailPinnedPosition = null
        return false
      }

      this.#galleryGroup = galleryGroupForMedia({
        alt: targetMedia.querySelector('[alt]')?.getAttribute('alt') ?? '',
        mediaType: targetMedia.getAttribute('data-media-type'),
      })
      this.#preservedGalleryPosition = targetPosition
      this.#variantThumbnailPinnedPosition = targetPosition
      this.#galleryThumbnailWindowStartIndex = 0
      this.#galleryThumbnailsExpanded = false
      this.#configureGallery()
      if (targetMedia.hidden) {
        this.#preservedGalleryPosition = null
        this.#variantThumbnailPinnedPosition = null
        return false
      }

      const currentPosition = this.#currentGalleryPosition(carousel, mediaItems)
      if (currentPosition !== targetPosition) {
        this.#selectGalleryPosition(carousel, targetPosition)
      }
      this.#syncGalleryNavigation(gallery, targetPosition)
      this.#configureGalleryThumbnails(gallery)
      queueMicrotask(() => {
        if (this.#preservedGalleryPosition === targetPosition) {
          this.#preservedGalleryPosition = null
        }
      })
      return true
    }

    #resolveVariantFeaturedPreview(mediaItems, filteredPositions = []) {
      const graphVariantId = this.#graph?.mainVariantId
      return resolveVariantFeaturedPreview({
        canonicalVariantId: readNamedFormControlValue(this.#form(), 'id'),
        filteredPositions: [...filteredPositions],
        graphVariantId,
        mediaItems,
        pending: this.#variantPending,
        variant: this.#mainVariant(graphVariantId),
      })
    }

    #selectGalleryPosition(carousel, position, { animate = false } = {}) {
      const index = Number.parseInt(position, 10) - 1
      if (!carousel || !Number.isFinite(index) || index < 0) return

      if (typeof carousel.select === 'function') {
        carousel.select(index, { animate, force: true })
        return
      }

      carousel.dispatchEvent(
        new CustomEvent('control:select', {
          bubbles: true,
          detail: { index },
        }),
      )
    }

    #navigateGalleryThumbnail(direction) {
      const gallery = this.querySelector('product-gallery')
      if (!gallery) return

      const wrapper = gallery.querySelector('.product-gallery__thumbnail-list-wrapper')
      const thumbnailList = wrapper?.querySelector('.product-gallery__thumbnail-list')
      const visibleCount = [
        ...(thumbnailList?.querySelectorAll(':scope > .product-gallery__thumbnail') ?? []),
      ].filter(
        (thumbnail) =>
          !thumbnail.hasAttribute('data-main-product-figma-gallery-thumbnail-filter-hidden'),
      ).length
      const thumbnailLimit = Math.max(
        1,
        Math.floor((Math.max(wrapper?.clientWidth ?? 0, 124) - 48 + 8) / 76),
      )
      this.#galleryThumbnailsExpanded = false
      this.#galleryThumbnailWindowStartIndex = resolveGalleryThumbnailWindowStart(
        visibleCount,
        thumbnailLimit,
        this.#galleryThumbnailWindowStartIndex,
        direction,
      )
      this.#configureGalleryThumbnails(gallery, { followCurrent: false })
    }

    #syncVariantThumbnailPreview(
      gallery,
      {
        currentPosition,
        followCurrent = true,
        thumbnailLimit = 7,
        thumbnails = [],
        visiblePositions = [],
        windowStartIndex = null,
      } = {},
    ) {
      const wrapper = gallery.querySelector('.product-gallery__thumbnail-list-wrapper')
      const previewControl = this.querySelector(
        '[data-main-product-figma-variant-thumbnail-preview]',
      )
      if (!wrapper || !previewControl || thumbnails.length < 2) {
        return reconcileVariantThumbnailPresentation({
          currentPosition,
          expanded: this.#galleryThumbnailsExpanded,
          followCurrent,
          previewControl,
          resolution: null,
          thumbnailLimit,
          thumbnails,
          visiblePositions,
          windowStartIndex,
        })
      }
      if (wrapper.firstElementChild !== previewControl) wrapper.prepend(previewControl)
      const mediaItems = [...gallery.querySelectorAll('.product-gallery__media')]
      const visible = new Set(visiblePositions)
      const filteredPositions = mediaItems.flatMap((_, index) =>
        visible.has(index + 1) ? [] : [index + 1],
      )
      const resolution = this.#resolveVariantFeaturedPreview(mediaItems, filteredPositions)
      const existingProjectionOwnsCurrent =
        previewControl.hidden !== true &&
        Number.parseInt(
          previewControl.getAttribute('data-main-product-figma-variant-thumbnail-target'),
          10,
        ) === Number.parseInt(currentPosition, 10)
      const projectionOwnsCurrent =
        existingProjectionOwnsCurrent ||
        Number.parseInt(resolution?.targetPosition, 10) === Number.parseInt(currentPosition, 10)
      return reconcileVariantThumbnailPresentation({
        currentPosition,
        expanded: this.#galleryThumbnailsExpanded,
        followCurrent: followCurrent && !projectionOwnsCurrent,
        previewControl,
        resolution,
        thumbnailLimit,
        thumbnails,
        visiblePositions,
        windowStartIndex,
      })
    }

    #restoreVariantThumbnailAccessibility(gallery) {
      const thumbnailList = gallery?.querySelector('.product-gallery__thumbnail-list')
      const affectedSelector = [
        '[data-main-product-figma-variant-thumbnail-obscured]',
        '[data-main-product-figma-variant-thumbnail-proxied]',
      ].join(',')
      const thumbnails = thumbnailList
        ? [...thumbnailList.querySelectorAll(':scope > .product-gallery__thumbnail')]
        : [...(gallery?.querySelectorAll(affectedSelector) ?? [])]
      clearVariantThumbnailPreview({
        previewControl: this.querySelector('[data-main-product-figma-variant-thumbnail-preview]'),
        thumbnails,
      })
    }

    #clearVariantThumbnailPreview({ reconcile = false } = {}) {
      const gallery = this.querySelector('product-gallery')
      if (gallery) {
        if (reconcile) {
          const thumbnailList = gallery.querySelector('.product-gallery__thumbnail-list')
          const thumbnails = [
            ...(thumbnailList?.querySelectorAll(':scope > .product-gallery__thumbnail') ?? []),
          ]
          const visiblePositions = thumbnails.flatMap((thumbnail, index) =>
            thumbnail.hasAttribute('data-main-product-figma-gallery-thumbnail-filter-hidden')
              ? []
              : [index + 1],
          )
          const mediaItems = [...gallery.querySelectorAll('.product-gallery__media')]
          reconcileVariantThumbnailPresentation({
            currentPosition: this.#currentGalleryPosition(
              gallery.querySelector('media-carousel'),
              mediaItems,
            ),
            expanded: this.#galleryThumbnailsExpanded,
            followCurrent: false,
            previewControl: this.querySelector(
              '[data-main-product-figma-variant-thumbnail-preview]',
            ),
            resolution: null,
            thumbnails,
            visiblePositions,
            windowStartIndex: this.#galleryThumbnailWindowStartIndex,
          })
          return
        }
        this.#restoreVariantThumbnailAccessibility(gallery)
        return
      }
      clearVariantThumbnailPreview({
        previewControl: this.querySelector('[data-main-product-figma-variant-thumbnail-preview]'),
      })
    }

    #syncVariantThumbnailPreviewState(gallery, currentPosition) {
      const previewControl = gallery.querySelector(
        '[data-main-product-figma-variant-thumbnail-preview]',
      )
      const thumbnailList = gallery.querySelector('.product-gallery__thumbnail-list')
      const thumbnails = [
        ...(thumbnailList?.querySelectorAll(':scope > .product-gallery__thumbnail') ?? []),
      ]
      if (!previewControl) return null
      return applyVariantThumbnailCurrentPresentation({
        currentPosition,
        previewControl,
        thumbnails,
      })
    }

    #syncGalleryNavigation(gallery, requestedPosition) {
      const navigation = this.querySelector('[data-role="gallery-navigation"]')
      const mediaItems = [...gallery.querySelectorAll('.product-gallery__media')]
      const visiblePositions = mediaItems.flatMap((item, index) => (item.hidden ? [] : [index + 1]))
      const state = resolveGalleryNavigationState(visiblePositions, requestedPosition)
      this.#syncVariantThumbnailPreviewState(gallery, state.currentPosition)
      const previewControl = gallery.querySelector(
        '[data-main-product-figma-variant-thumbnail-preview]',
      )
      const counterPresentation = resolveGalleryCounterPresentation(state, {
        previewTargetPosition: previewControl?.getAttribute(
          'data-main-product-figma-variant-thumbnail-target',
        ),
        previewVisible: Boolean(
          previewControl && !previewControl.hidden && previewControl.getClientRects().length,
        ),
        visiblePositions,
      })
      const current = navigation?.querySelector('[data-main-product-figma-gallery-current]')
      const total = navigation?.querySelector('[data-main-product-figma-gallery-total]')
      if (current) current.textContent = String(counterPresentation.current)
      if (total) total.textContent = String(counterPresentation.total)

      if (!navigation || state.total <= 1) {
        navigation?.setAttribute('hidden', '')
        return state
      }

      navigation.removeAttribute('hidden')
      const previous = navigation.querySelector('[data-main-product-figma-gallery-previous]')
      const next = navigation.querySelector('[data-main-product-figma-gallery-next]')
      if (previous) previous.disabled = state.previousPosition === null
      if (next) next.disabled = state.nextPosition === null
      return state
    }

    #configureGalleryThumbnails(gallery, { followCurrent = true } = {}) {
      const thumbnailList = gallery.querySelector('.product-gallery__thumbnail-list')
      const wrapper = thumbnailList?.closest('.product-gallery__thumbnail-list-wrapper')
      const toggle = this.querySelector('[data-main-product-figma-gallery-thumbnail-toggle]')
      const previous = this.querySelector('[data-main-product-figma-gallery-thumbnail-previous]')
      const next = this.querySelector('[data-main-product-figma-gallery-thumbnail-next]')
      if (!thumbnailList || !wrapper || !toggle) {
        toggle?.setAttribute('hidden', '')
        previous?.setAttribute('hidden', '')
        next?.setAttribute('hidden', '')
        return
      }

      const thumbnails = [...thumbnailList.querySelectorAll(':scope > .product-gallery__thumbnail')]
      const thumbnailLimit = Math.max(
        1,
        Math.floor((Math.max(wrapper.clientWidth, 124) - 48 + 8) / 76),
      )
      wrapper.style.setProperty('--mpf-thumbnail-visible-count', String(thumbnailLimit))
      wrapper.style.setProperty(
        '--mpf-thumbnail-toggle-offset',
        `${28 + (thumbnailLimit - 1) * 76}px`,
      )
      const visiblePositions = thumbnails.flatMap((thumbnail, index) =>
        thumbnail.hasAttribute('data-main-product-figma-gallery-thumbnail-filter-hidden')
          ? []
          : [index + 1],
      )
      const hasThumbnailOverflow = visiblePositions.length > thumbnailLimit
      for (const button of [previous, next]) {
        if (!button) continue
        if (button.parentElement !== wrapper) wrapper.append(button)
        button.toggleAttribute('hidden', !hasThumbnailOverflow)
      }
      const mediaItems = [...gallery.querySelectorAll('.product-gallery__media')]
      const currentPosition = this.#currentGalleryPosition(
        gallery.querySelector('media-carousel'),
        mediaItems,
      )
      const pinnedProjectionOwnsCurrent =
        Number.parseInt(this.#variantThumbnailPinnedPosition, 10) ===
        Number.parseInt(currentPosition, 10)
      const { thumbnailState: state } = this.#syncVariantThumbnailPreview(gallery, {
        currentPosition,
        followCurrent: followCurrent && !this.#variantPending && !pinnedProjectionOwnsCurrent,
        thumbnailLimit,
        thumbnails,
        visiblePositions,
        windowStartIndex: this.#galleryThumbnailWindowStartIndex,
      })
      this.#galleryThumbnailWindowStartIndex = state.windowStartIndex
      wrapper.style.setProperty('--mpf-thumbnail-track-offset', `${-state.windowStartIndex * 76}px`)
      if (previous) previous.disabled = !hasThumbnailOverflow
      if (next) next.disabled = !hasThumbnailOverflow

      if (!state.hasToggle) {
        this.#galleryThumbnailsExpanded = false
        toggle.setAttribute('aria-expanded', 'false')
        toggle.setAttribute('hidden', '')
        return
      }

      if (toggle.parentElement !== wrapper) wrapper.append(toggle)
      if (!thumbnailList.id) {
        const sectionId = this.getAttribute('data-section-id') || 'section'
        thumbnailList.id = `main-product-figma-gallery-thumbnails-${sectionId}`
      }
      this.#galleryThumbnailsExpanded = state.expanded
      toggle.setAttribute('aria-controls', thumbnailList.id)
      toggle.setAttribute('aria-expanded', String(state.expanded))
      toggle.removeAttribute('hidden')

      const countTemplate = toggle.getAttribute('data-count-label') || '+__COUNT__'
      const moreLabel = toggle.getAttribute('data-more-label') || ''
      const fewerLabel = toggle.getAttribute('data-fewer-label') || ''
      const visibleLabel = state.expanded
        ? '−'
        : countTemplate.replace('__COUNT__', String(state.remaining))
      const accessibleLabel = state.expanded
        ? fewerLabel
        : `${moreLabel} (${state.remaining})`.trim()
      const label = toggle.querySelector('[data-main-product-figma-gallery-thumbnail-toggle-label]')
      if (label) label.textContent = visibleLabel
      if (accessibleLabel) toggle.setAttribute('aria-label', accessibleLabel)
    }

    #applyGalleryFilter(gallery, mediaItems, filteredPositions) {
      const carousel = gallery.querySelector('media-carousel')
      const actualPosition = this.#currentGalleryPosition(carousel, mediaItems)
      const presentation = applyGalleryFilterPresentation(gallery, filteredPositions)
      this.#configureGalleryThumbnails(gallery)

      const transition = resolveGallerySelectionTransition({
        actualPosition,
        preferredPosition: null,
        visiblePositions: presentation.visiblePositions,
      })
      this.#syncGalleryNavigation(gallery, transition.finalPosition)
      if (carousel && transition.shouldSelect) {
        this.#selectGalleryPosition(carousel, transition.finalPosition)
      }
    }

    #openDialog(trigger) {
      const dialog = this.querySelector('[data-role="dialog"]')
      const title = dialog?.querySelector('[id^="main-product-figma-dialog-title-"]')
      const body = dialog?.querySelector('[data-main-product-figma-dialog-body]')
      const templateId = trigger.getAttribute('data-dialog-content-id')
      const template = templateId ? this.querySelector(`#${CSS.escape(templateId)}`) : null
      if (!dialog || !title || !body || !template?.content) return

      this.#dialogTrigger = trigger
      title.textContent = trigger.getAttribute('data-dialog-title') ?? ''
      body.replaceChildren(template.content.cloneNode(true))
      if (!dialog.open) dialog.showModal()
      syncDialogScrollLock()
    }

    #closeDialog(restoreFocus = true) {
      const dialog = this.querySelector('[data-role="dialog"]')
      if (dialog?.open) dialog.close()
      syncDialogScrollLock()
      if (restoreFocus) {
        if (this.#dialogTrigger?.isConnected) {
          this.#dialogTrigger?.focus()
        } else {
          this.querySelector(
            '[data-main-product-figma-dialog-trigger], [data-role="info-rail"] button, [data-role="info-rail"] a',
          )?.focus()
        }
      }
      this.#dialogTrigger = null
    }

    #form() {
      const formId = this.#config.formId || this.getAttribute('data-form-id')
      return formId ? document.getElementById(formId) : null
    }

    #captureAccessoryFocus() {
      const activeElement = document.activeElement
      if (!activeElement || !this.contains(activeElement)) return null

      for (const attribute of [
        'data-main-product-figma-addon',
        'data-main-product-figma-addon-variant',
        'data-main-product-figma-addon-quantity',
        'data-main-product-figma-addon-decrease',
        'data-main-product-figma-addon-increase',
      ]) {
        const value = activeElement.getAttribute?.(attribute)
        if (value) return { attribute, value }
      }
      return null
    }

    #restoreAccessoryFocus(token) {
      if (!token) return
      const control = [...this.querySelectorAll(`[${token.attribute}]`)].find(
        (candidate) => candidate.getAttribute(token.attribute) === token.value,
      )
      control?.focus({ preventScroll: true })
    }

    #mainVariant(id) {
      return (this.#config.mainVariants ?? []).find((variant) => toId(variant.id) === toId(id))
    }

    #addonsForMainVariant(variant) {
      return variant?.addons ?? []
    }

    #createGraphForMainVariant(id) {
      const variant = resolveMainVariant(this.#config.mainVariants, id)
      const quantity = this.#form()?.elements?.quantity?.value
      return createSelectionGraph({
        mainVariant: variant,
        mainQuantity: quantity,
        addons: this.#addonsForMainVariant(variant),
        defaultSelected: this.#config.defaultSelected === true,
      })
    }

    #selectMainVariant(id) {
      const variant = this.#mainVariant(id)
      if (!variant || !this.#graph) return
      const canonicalQuantity = this.#form()?.elements?.quantity?.value ?? 1
      this.#graph = reconcileSelectionGraph(this.#graph, {
        mainVariant: variant,
        addons: this.#addonsForMainVariant(variant),
        defaultSelected: this.#config.defaultSelected === true,
      })
      this.#graph = setMainQuantity(this.#graph, canonicalQuantity)
      this.#config.currentVariantId = variant.id
      this.#render()
    }

    #confirmMainVariant(variantId) {
      const requestedVariantId = toId(variantId)
      const formVariantId = toId(readNamedFormControlValue(this.#form(), 'id'))
      if (
        !requestedVariantId ||
        !formVariantId ||
        formVariantId !== requestedVariantId ||
        !this.#mainVariant(formVariantId)
      ) {
        return false
      }

      if (this.#variantPendingTimer !== null) window.clearTimeout(this.#variantPendingTimer)
      this.#variantPendingTimer = null
      this.#variantPending = false
      this.removeAttribute('data-variant-pending')
      this.#clearVariantFailure()
      this.#selectMainVariant(formVariantId)
      this.#setInteractionLock('variant', false)
      return true
    }

    #setVariantPending(pending) {
      if (this.#variantPendingTimer !== null) window.clearTimeout(this.#variantPendingTimer)
      this.#variantPendingTimer = null
      if (pending) {
        this.#clearVariantFailure()
      }
      this.#variantPending = pending
      this.toggleAttribute('data-variant-pending', pending)
      this.#setInteractionLock('variant', pending)
      if (pending) this.#clearVariantThumbnailPreview({ reconcile: true })
      if (!pending || !this.#lifecycle) return

      const lifecycle = this.#lifecycle
      this.#variantPendingTimer = lifecycle.trackTimeout(
        window.setTimeout(() => {
          if (this.#lifecycle !== lifecycle || !this.#variantPending) return
          this.#recoverVariantConfirmationFailure()
        }, VARIANT_CONFIRM_TIMEOUT_MS),
      )
    }

    #recoverVariantConfirmationFailure() {
      this.#restoreCanonicalPackageSelection()

      this.#variantPending = false
      this.#variantPendingTimer = null
      this.#preservedGalleryPosition = null
      this.removeAttribute('data-variant-pending')
      this.#clearVariantThumbnailPreview({ reconcile: true })
      this.#setInteractionLock('variant', false)
      this.#variantFailed = true
      this.setAttribute('data-variant-error', '')
      this.#applyVariantFailureLock()
      this.#setStatus(this.#config.labels?.variantChangeFailed ?? '', 'error')
    }

    #restoreCanonicalPackageSelection() {
      const currentVariant = this.#mainVariant(this.#graph?.mainVariantId)
      const packageInputs = this.querySelectorAll(
        `input[data-option-position="${this.#config.packageOptionPosition}"]`,
      )
      for (const input of packageInputs) {
        const selected = toId(input.value) === toId(currentVariant?.optionValueId)
        input.checked = selected
        input.nextElementSibling?.toggleAttribute('data-selected', selected)
      }
    }

    #variantActionControls() {
      return new Set([
        ...(this.#form()?.querySelectorAll(
          'buy-buttons button[type="submit"], buy-buttons input[type="submit"]',
        ) ?? []),
        ...this.querySelectorAll(
          '[data-main-product-figma-action="add"], [data-main-product-figma-action="shop-now"]',
        ),
      ])
    }

    #applyVariantFailureLock() {
      if (!this.#variantFailed) return
      for (const control of this.#variantActionControls()) {
        if (!control.hasAttribute('data-main-product-figma-variant-error-lock')) {
          control.setAttribute(
            'data-main-product-figma-variant-error-base-disabled',
            String(control.disabled),
          )
          control.setAttribute('data-main-product-figma-variant-error-lock', '')
        }
        if (!control.disabled) control.disabled = true
      }
    }

    #clearVariantFailure() {
      for (const control of this.querySelectorAll('[data-main-product-figma-variant-error-lock]')) {
        control.disabled =
          control.getAttribute('data-main-product-figma-variant-error-base-disabled') === 'true'
        control.removeAttribute('data-main-product-figma-variant-error-base-disabled')
        control.removeAttribute('data-main-product-figma-variant-error-lock')
      }
      if (this.#variantFailed) this.#setStatus('')
      this.#variantFailed = false
      this.removeAttribute('data-variant-error')
    }

    #refreshAfterRerender() {
      if (!this.isConnected) return
      const nextConfig = parseConfig(this)
      if (Array.isArray(nextConfig.mainVariants)) this.#config = nextConfig
      const formVariantId = readNamedFormControlValue(this.#form(), 'id')
      const confirmed = this.#confirmMainVariant(formVariantId)
      if (!confirmed) this.#clearVariantThumbnailPreview({ reconcile: true })
      this.#configureGallery()
      if (confirmed) this.#selectVariantFeaturedMedia()
      this.#bindMediaRailSticky()
      this.#bindFixedBar()
    }

    #rememberPackageVariantViewport(token, { replace = false } = {}) {
      if (!token) return this.#packageVariantViewport
      if (this.#packageVariantViewport && replace !== true) {
        return this.#packageVariantViewport
      }

      this.#packageVariantViewport = token
      const lifecycle = this.#lifecycle
      if (lifecycle) {
        lifecycle.trackTimeout(
          window.setTimeout(() => {
            if (this.#packageVariantViewport === token) {
              this.#packageVariantViewport = null
            }
          }, VARIANT_CONFIRM_TIMEOUT_MS),
        )
      }
      return token
    }

    #onChange(event) {
      const input = event.target
      if (!input || !this.#graph) return
      const packageSelector = `input[data-option-position="${this.#config.packageOptionPosition}"]`
      const isPackageInput = input.matches?.(packageSelector) === true

      if (isPackageInput && (input.disabled || input.getAttribute?.('aria-disabled') === 'true')) {
        event.preventDefault?.()
        event.stopImmediatePropagation?.()
        this.#restoreCanonicalPackageSelection()
        return
      }
      if (input.disabled || input.getAttribute?.('aria-disabled') === 'true') return

      if (input.name === 'quantity' && input.form === this.#form()) {
        this.#graph = setMainQuantity(this.#graph, input.value)
        this.#render()
        return
      }

      if (isPackageInput) {
        this.#rememberPackageVariantViewport(
          capturePackageVariantViewport({
            documentRef: document,
            input,
            packageOptionPosition: this.#config.packageOptionPosition,
            releaseFocus: false,
            root: this,
            windowRef: window,
          }),
        )
        window.setTimeout(() => {
          if (!this.isConnected || !input.checked) return

          const requestedVariant = resolveMainVariantForOption(
            this.#config.mainVariants,
            input.value,
            readSelectedOptionValueIds(this.#form(), input),
          )
          const formVariantId = readNamedFormControlValue(this.#form(), 'id')
          if (
            requestedVariant &&
            toId(formVariantId) === toId(requestedVariant.id) &&
            this.#confirmMainVariant(requestedVariant.id)
          ) {
            this.#selectVariantFeaturedMedia()
            return
          }

          this.#setVariantPending(true)
        }, 0)
        return
      }

      const productId = input.getAttribute?.('data-main-product-figma-addon')
      if (productId) {
        this.#graph = setAddonSelection(this.#graph, productId, input.checked)
        this.#render()
        return
      }

      const variantProductId = input.getAttribute?.('data-main-product-figma-addon-variant')
      if (variantProductId) {
        const viewportScroll = captureViewportScroll(window)
        this.#graph = setAddonVariant(this.#graph, variantProductId, input.value)
        this.#render()
        restoreViewportScroll(window, viewportScroll)
        this.#lifecycle?.trackTimeout(
          window.setTimeout(() => {
            if (this.isConnected) restoreViewportScroll(window, viewportScroll)
          }, 0),
        )
        return
      }

      const quantityProductId = input.getAttribute?.('data-main-product-figma-addon-quantity')
      if (quantityProductId) {
        this.#graph = setAddonQuantity(this.#graph, quantityProductId, input.value)
        this.#render()
      }
    }

    #onSubmit(event) {
      const form = this.#form()
      if (!form || event.target !== form) return

      const intent = this.#submissionIntent(event.submitter, form)
      if (!shouldInterceptBundledSubmit(this.#graph, intent)) return

      event.preventDefault()
      event.stopImmediatePropagation()
      if (!form.checkValidity()) {
        form.reportValidity()
        return
      }

      const formEntries = [...new FormData(form).entries()]
      const formVariantId = toId(readNamedFormControlValue(form, 'id'))
      const formVariant = this.#mainVariant(formVariantId)
      if (!formVariant || formVariant.available !== true) {
        this.#recoverVariantConfirmationFailure()
        return
      }
      if (formVariant.id !== this.#graph.mainVariantId) {
        this.#selectMainVariant(formVariant.id)
      }
      this.#graph = projectCanonicalFormState(this.#graph, formEntries, {
        mainVariantId: formVariant.id,
      })
      const selectionSnapshot = this.#graph
      this.#submissionGate.run(() => this.#submitSelection(form, intent, selectionSnapshot))
    }

    #submissionIntent(submitter, form) {
      if (!submitter) return null
      if (submitter.getAttribute?.('data-main-product-figma-action') === 'shop-now') {
        return 'shop-now'
      }
      if (submitter.getAttribute?.('data-main-product-figma-action') === 'add') return 'add'
      if (
        submitter.form === form &&
        submitter.matches?.('button[type="submit"], input[type="submit"]') &&
        submitter.closest?.('buy-buttons')
      ) {
        return 'add'
      }
      return 'unknown'
    }

    async #submitSelection(form, intent, selectionSnapshot) {
      const lifecycle = this.#lifecycle
      if (!lifecycle || lifecycle.signal.aborted) return
      this.#setBusy(true)
      this.#setStatus(this.#config.labels?.adding ?? '', 'busy')

      try {
        const routesRoot = window.Shopify?.routes?.root ?? '/'
        await submitSelectionIntent({
          cartType: window.themeVariables?.settings?.cartType,
          countryCode: this.#config.countryCode,
          documentRef: document,
          fetchImpl: window.fetch.bind(window),
          form,
          graph: selectionSnapshot,
          intent,
          locationRef: window.location,
          pageType: window.themeVariables?.settings?.pageType,
          routesRoot,
          shopDomain: this.#config.shopDomain,
          signal: lifecycle.signal,
        })
        if (this.#lifecycle !== lifecycle || lifecycle.signal.aborted || !this.isConnected) return
        this.#setStatus(this.#config.labels?.added ?? '', 'success')
      } catch (error) {
        if (this.#lifecycle !== lifecycle || lifecycle.signal.aborted || !this.isConnected) return
        const failure = resolveCartSubmissionFailure(error, this.#config.labels)
        if (!failure.aborted) {
          this.#setStatus(failure.message, failure.tone)
          if (failure.refreshCart) {
            document.dispatchEvent(new CustomEvent('cart:refresh'))
          }
        }
      } finally {
        if (this.#lifecycle === lifecycle) this.#setBusy(false)
      }
    }

    #setBusy(busy) {
      const form = this.#form()
      const buttons = new Set([
        ...(form?.querySelectorAll(
          'buy-buttons button[type="submit"], buy-buttons input[type="submit"]',
        ) ?? []),
        ...this.querySelectorAll(
          '[data-main-product-figma-action="add"], [data-main-product-figma-action="shop-now"]',
        ),
      ])
      for (const button of buttons) {
        if (busy) button.setAttribute('aria-busy', 'true')
        else button.removeAttribute('aria-busy')
      }
      this.toggleAttribute('data-busy', busy)
      this.#setInteractionLock('submit', busy)
    }

    #setControlBaseDisabled(control, disabled) {
      if (!control) return
      if (control.hasAttribute('data-main-product-figma-interaction-lock')) {
        control.setAttribute('data-main-product-figma-base-disabled', String(disabled))
        control.disabled = true
        return
      }
      control.disabled = disabled
    }

    #setInteractionLock(name, locked) {
      if (locked) this.#interactionLocks.add(name)
      else this.#interactionLocks.delete(name)
      this.#applyInteractionLock()
    }

    #applyInteractionLock() {
      const locked = this.#interactionLocks.size > 0
      const form = this.#form()
      const controls = new Set([
        ...(form?.querySelectorAll(
          'buy-buttons button[type="submit"], buy-buttons input[type="submit"], input[data-option-position]',
        ) ?? []),
        ...this.querySelectorAll(
          '[data-role="package-selector"] input, [data-role="accessories"] button, [data-role="accessories"] input, [data-role="accessories"] select, [data-role="care"] button, [data-role="care"] input, [data-role="care"] select, [data-main-product-figma-action="add"], [data-main-product-figma-action="shop-now"]',
        ),
      ])

      for (const control of controls) {
        if (locked) {
          if (!control.hasAttribute('data-main-product-figma-interaction-lock')) {
            control.setAttribute('data-main-product-figma-base-disabled', String(control.disabled))
            control.setAttribute('data-main-product-figma-interaction-lock', '')
          }
          if (!control.disabled) control.disabled = true
          continue
        }

        if (control.hasAttribute('data-main-product-figma-interaction-lock')) {
          const disabled = control.getAttribute('data-main-product-figma-base-disabled') === 'true'
          if (control.disabled !== disabled) control.disabled = disabled
          control.removeAttribute('data-main-product-figma-base-disabled')
          control.removeAttribute('data-main-product-figma-interaction-lock')
        }
      }

      this.setAttribute('aria-busy', String(locked))
    }

    #setStatus(message, state = '') {
      const status = this.querySelector('[data-role="cart-status"]')
      if (!status) return
      status.textContent = message
      if (state) status.setAttribute('data-state', state)
      else status.removeAttribute('data-state')
    }

    #render() {
      if (!this.#graph) return
      const focusToken = this.#captureAccessoryFocus()
      this.#enhancePackageCards()
      this.#renderInformationPoints()
      this.#renderAccessories()
      this.#renderCare()
      this.#renderSummary()
      this.#renderFixedBar()
      this.#applyInteractionLock()
      this.#applyVariantFailureLock()
      this.#restoreAccessoryFocus(focusToken)
    }

    #renderInformationPoints() {
      const mainVariant = this.#mainVariant(this.#graph.mainVariantId)
      for (const row of this.querySelectorAll('[data-main-product-figma-points]')) {
        const content = row.querySelector('.mpf-info-panel__content')
        const templateId = row.getAttribute('data-dialog-content-id')
        const template = templateId ? this.querySelector(`#${CSS.escape(templateId)}`) : null
        const dialogCopy = template?.content?.querySelector(
          '[data-main-product-figma-points-dialog-copy]',
        )
        const copy = resolveRewardEstimate({
          template: row.getAttribute('data-points-template'),
          variantPrice: mainVariant?.price,
        })
        row.toggleAttribute('hidden', copy === '')
        if (content) content.innerHTML = copy
        if (dialogCopy) dialogCopy.innerHTML = copy
      }
    }

    #enhancePackageCards() {
      const position = this.#config.packageOptionPosition
      if (!position) return
      const selector = `input[data-option-position="${position}"]`
      const heading = this.querySelector('[id^="main-product-figma-package-heading-"]')

      for (const input of this.querySelectorAll(selector)) {
        const label = input.nextElementSibling
        if (!label) continue
        const variant = resolveMainVariantForOption(
          this.#config.mainVariants,
          input.value,
          readSelectedOptionValueIds(this.#form(), input),
        )
        const nativeUnavailable = label.classList?.contains?.('is-disabled') === true
        const available = variant
          ? variant.available === true && !nativeUnavailable
          : !nativeUnavailable && input.disabled !== true

        const fieldset = input.closest('fieldset')
        fieldset?.setAttribute('data-main-product-figma-package-fieldset', '')
        if (heading?.id) fieldset?.setAttribute('aria-labelledby', heading.id)
        this.#setControlBaseDisabled(input, !available)
        label.setAttribute('data-role', 'package-card')
        label.setAttribute('data-available', String(available))
        if (input.disabled) label.setAttribute('aria-disabled', 'true')
        else label.removeAttribute('aria-disabled')
        label.toggleAttribute('data-selected', input.checked)
        label.querySelector('[data-main-product-figma-package-meta]')?.remove()
        label.querySelector('[data-main-product-figma-package-saving]')?.remove()
        if (!variant) continue
        label.firstElementChild?.classList.add('mpf-package-card__title')
        const meta = element('span', {
          'data-main-product-figma-package-meta': '',
          class: 'mpf-package-card__meta',
        })
        if (variant.subtitle) {
          meta.append(element('span', { class: 'mpf-package-card__subtitle' }, variant.subtitle))
        }
        if (variant.formattedPrice) {
          meta.append(element('span', { class: 'mpf-package-card__price' }, variant.formattedPrice))
        }
        if (variant.formattedCompareAtPrice) {
          meta.append(
            element(
              's',
              { class: 'mpf-package-card__compare-price' },
              variant.formattedCompareAtPrice,
            ),
          )
        }
        label.append(meta)
        if (variant.savingLabel) {
          label.append(
            element(
              'span',
              {
                class: 'mpf-package-card__saving',
                'data-main-product-figma-package-saving': '',
              },
              variant.savingLabel,
            ),
          )
        }
      }
    }

    #renderAccessories() {
      const host = this.querySelector('[data-role="accessory-list"]')
      if (!host) return
      host.replaceChildren()

      const fbtProductIds = this.#graph.visibleAddonProductIds.filter(
        (productId) => this.#graph.addonState[productId]?.kind === 'fbt',
      )
      for (const productId of fbtProductIds) {
        const state = this.#graph.addonState[productId]
        const selectedVariant = selectAddonDisplayVariant(state)
        const hasVariantSelector = shouldShowAddonVariantSelector(state)
        const card = element('div', {
          class: 'mpf-accessory-card',
          'data-role': 'accessory-card',
          'data-product-id': productId,
          'data-addon-kind': state.kind,
          'data-selected': String(state.selected),
          'data-available': String(state.available),
          'data-variant-selector': String(hasVariantSelector),
          'aria-disabled': state.available ? null : 'true',
        })
        const checkboxId = `${this.id || 'main-product'}-addon-${productId}`
        const checkbox = element('input', {
          id: checkboxId,
          type: 'checkbox',
          'data-main-product-figma-addon': productId,
          'data-addon-kind': state.kind,
          'data-lp-track': state.selected ? 'unselect_fbt_item' : 'select_fbt_item',
          'data-lp-track-context': 'pdp_accessories',
          'data-lp-track-source': 'vendor',
          'data-lp-product-id': productId,
          checked: state.selected,
          disabled: !state.available,
        })
        const label = element('label', { for: checkboxId, class: 'mpf-accessory-card__label' })
        if (state.featuredImage) {
          label.append(
            element('img', {
              src: state.featuredImage,
              alt: '',
              loading: 'lazy',
              width: '60',
              height: '60',
            }),
          )
        }
        const content = element('span', { class: 'mpf-accessory-card__content' })
        content.append(element('strong', {}, state.title))
        if (state.subtitle) {
          content.append(element('span', { class: 'mpf-accessory-card__subtitle' }, state.subtitle))
        }
        const selectedUnitPrice = resolveQuantityUnitPrice(selectedVariant, state.quantity)
        const selectedRegularUnitPrice = Math.max(
          Number(selectedVariant?.compareAtPrice ?? 0),
          Number(selectedVariant?.price ?? 0),
          selectedUnitPrice,
        )
        const prices = element('span', { class: 'mpf-accessory-card__prices' })
        prices.append(
          element(
            'span',
            { class: 'mpf-accessory-card__price' },
            formatMoney(selectedUnitPrice, this.#config),
          ),
        )
        if (selectedRegularUnitPrice > selectedUnitPrice) {
          prices.append(
            element(
              's',
              { class: 'mpf-accessory-card__compare-price' },
              formatMoney(selectedRegularUnitPrice, this.#config),
            ),
          )
        }
        content.append(prices)
        label.append(content)
        card.append(checkbox, label)

        const controls = element('div', { class: 'mpf-accessory-card__controls' })
        if (hasVariantSelector) {
          const select = element('select', {
            'data-main-product-figma-addon-variant': productId,
            'aria-label': this.#config.labels?.variant ?? '',
            disabled: !state.available,
          })
          for (const variant of state.variants) {
            const option = element('option', { value: variant.id }, variant.title)
            option.disabled = !resolveVariantQuantityRule(variant).purchasable
            option.selected = variant.id === state.resolvedVariantId
            select.append(option)
          }
          select.value = state.resolvedVariantId ?? ''
          controls.append(select)
        }

        const quantityRule = resolveVariantQuantityRule(selectedVariant)
        const quantityControl = element('div', { class: 'mpf-accessory-card__quantity' })
        const decrease = element(
          'button',
          {
            type: 'button',
            'data-main-product-figma-addon-decrease': productId,
            'aria-label': this.#config.labels?.decreaseQuantity ?? '',
            disabled:
              !state.selected || !quantityRule.purchasable || state.quantity <= quantityRule.min,
          },
          '−',
        )
        const quantityAttributes = {
          type: 'number',
          min: String(quantityRule.min),
          step: String(quantityRule.increment),
          value: String(state.quantity),
          'aria-label': this.#config.labels?.quantity ?? '',
          'data-main-product-figma-addon-quantity': productId,
          disabled: !state.selected || !quantityRule.purchasable,
        }
        if (quantityRule.max !== null) quantityAttributes.max = String(quantityRule.max)
        const quantity = element('input', quantityAttributes)
        const increase = element(
          'button',
          {
            type: 'button',
            'data-main-product-figma-addon-increase': productId,
            'aria-label': this.#config.labels?.increaseQuantity ?? '',
            disabled:
              !state.selected ||
              !quantityRule.purchasable ||
              (quantityRule.max !== null && state.quantity >= quantityRule.max),
          },
          '+',
        )
        quantityControl.append(decrease, quantity, increase)
        controls.append(quantityControl)
        content.append(controls)
        host.append(card)
      }

      host
        .closest('[data-role="accessories"]')
        ?.toggleAttribute('hidden', fbtProductIds.length === 0)
    }

    #renderCare() {
      const host = this.querySelector('[data-role="care"]')
      const list = host?.querySelector('[data-role="care-list"]')
      if (!host || !list) return
      list.replaceChildren()

      const careStates = this.#graph.visibleAddonProductIds
        .map((productId) => this.#graph.addonState[productId])
        .filter((state) => state?.kind === 'care')
      if (careStates.length === 0) {
        host.setAttribute('hidden', '')
        return
      }

      for (const state of careStates) {
        const productId = state.productId
        const selectedVariant = selectAddonDisplayVariant(state)
        const hasVariantSelector = shouldShowAddonVariantSelector(state)
        const unitPrice = resolveQuantityUnitPrice(selectedVariant, state.quantity)
        const compareAtPrice = Number(selectedVariant?.compareAtPrice ?? 0)
        const card = element('div', {
          class: 'mpf-care__card',
          'data-role': 'care-card',
          'data-product-id': productId,
          'data-addon-kind': 'care',
          'data-selected': String(state.selected),
        })
        const checkboxId = `${this.id || 'main-product'}-care-${productId}`
        const checkbox = element('input', {
          id: checkboxId,
          type: 'checkbox',
          'data-main-product-figma-addon': productId,
          'data-addon-kind': 'care',
          checked: state.selected,
          disabled: !state.available,
        })
        const label = element('label', { class: 'mpf-care__label', for: checkboxId })
        if (state.featuredImage) {
          label.append(
            element('img', {
              src: state.featuredImage,
              alt: '',
              loading: 'lazy',
              width: '80',
              height: '80',
            }),
          )
        }
        const content = element('span', { class: 'mpf-care__content' })
        content.append(element('strong', {}, state.title))
        const prices = element('span', { class: 'mpf-care__prices' })
        prices.append(
          element('span', { class: 'mpf-care__price' }, formatMoney(unitPrice, this.#config)),
        )
        if (compareAtPrice > unitPrice) {
          prices.append(
            element(
              's',
              { class: 'mpf-care__compare-price' },
              formatMoney(compareAtPrice, this.#config),
            ),
          )
        }
        content.append(prices)
        if (hasVariantSelector) {
          const select = element('select', {
            class: 'mpf-care__variant-selector',
            'data-main-product-figma-addon-variant': productId,
            'aria-label': this.#config.labels?.variant ?? '',
            disabled: !state.available,
          })
          for (const variant of state.variants) {
            const option = element('option', { value: variant.id }, variant.title)
            option.disabled = !resolveVariantQuantityRule(variant).purchasable
            option.selected = variant.id === state.resolvedVariantId
            select.append(option)
          }
          select.value = state.resolvedVariantId ?? ''
          content.append(select)
        }
        label.append(content)
        card.append(checkbox, label)

        const footer = element('div', { class: 'mpf-care__footer' })
        if (state.subtitle) {
          const contains = element('span')
          contains.append(
            element('strong', {}, this.#config.labels?.careContains ?? ''),
            document.createTextNode(` ${state.subtitle}`),
          )
          footer.append(contains)
        }
        if (state.url) {
          footer.append(element('a', { href: state.url }, this.#config.labels?.careLearnMore ?? ''))
        }
        card.append(footer)
        list.append(card)
      }

      host.removeAttribute('hidden')
    }

    #renderSummary() {
      const list = this.querySelector('[data-role="summary-lines"]')
      const total = this.querySelector('[data-role="summary-total-value"]')
      if (!list || !total) return

      const mainVariant = this.#mainVariant(this.#graph.mainVariantId)
      const summary = list.closest('[data-role="selection-summary"]')
      if (!mainVariant) {
        summary?.setAttribute('hidden', '')
        return
      }
      summary?.removeAttribute('hidden')
      list.replaceChildren()

      const model = buildSelectionSummaryModel(this.#graph, mainVariant, this.#config)
      for (const row of model.rows) {
        list.append(
          this.#summaryLine(row.label, row.detail, row.trailing, {
            image: row.image,
            main: row.main,
          }),
        )
      }

      total.textContent = formatSummaryMoney(model.total, this.#config)
      total.setAttribute('data-cents', String(model.total))
    }

    #summaryLine(label, detail, trailing, { image = '', main = false } = {}) {
      const item = element('li', {
        class: `mpf-summary__line${main ? ' mpf-summary__line--main' : ''}`,
      })
      if (main) {
        const imageHost = element('span', {
          class: 'mpf-summary__image',
          'data-role': 'summary-main-image',
        })
        if (image) {
          imageHost.append(
            element('img', {
              src: image,
              alt: '',
              loading: 'lazy',
              width: '64',
              height: '64',
            }),
          )
        }
        item.append(imageHost)
      }
      const copy = element('span', { class: 'mpf-summary__copy' })
      copy.append(element('strong', {}, label))
      if (detail) copy.append(element('span', {}, detail))
      item.append(copy)
      if (trailing) item.append(element('span', { class: 'mpf-summary__quantity' }, trailing))
      return item
    }

    #renderFixedBar() {
      const fixedPrice = this.querySelector('[data-role="fixed-bar-price"]')
      const fixedComparePrice = this.querySelector('[data-role="fixed-bar-compare-price"]')
      const fixedButton = this.querySelector('[data-main-product-figma-submit]')
      const fixedButtonIcon = fixedButton?.querySelector('[data-role="fixed-bar-add-icon"]')
      const fixedButtonLabel = fixedButton?.querySelector('[data-main-product-figma-submit-label]')
      const shopNowButtons = this.querySelectorAll('[data-main-product-figma-action="shop-now"]')
      const mainVariant = this.#mainVariant(this.#graph.mainVariantId)
      const totals = computeSelectionTotals(this.#graph, mainVariant)
      const mainUnavailable = this.#graph.mainVariantAvailable !== true
      if (fixedPrice) {
        fixedPrice.textContent = formatMoney(totals.price, this.#config)
      }
      if (fixedComparePrice) {
        fixedComparePrice.textContent = formatMoney(totals.compareAtPrice, this.#config)
        fixedComparePrice.toggleAttribute('hidden', !totals.hasDiscount)
      }
      if (fixedButton) {
        this.#setControlBaseDisabled(fixedButton, mainUnavailable)
        fixedButton.setAttribute('aria-disabled', String(mainUnavailable))
        fixedButtonIcon?.toggleAttribute('hidden', mainUnavailable)
        fixedButtonLabel?.toggleAttribute('hidden', !mainUnavailable)
        const buttonLabel =
          this.#graph.mainVariantAvailable === true
            ? (this.#config.labels?.addToCart ?? '')
            : (this.#config.labels?.soldOut ?? '')
        fixedButton.setAttribute('aria-label', buttonLabel)
        if (fixedButtonLabel) fixedButtonLabel.textContent = buttonLabel
      }
      for (const shopNowButton of shopNowButtons) {
        this.#setControlBaseDisabled(shopNowButton, mainUnavailable)
        shopNowButton.setAttribute('aria-disabled', String(mainUnavailable))
      }
    }
  }

  if (!window.customElements.get('main-product-figma')) {
    window.customElements.define('main-product-figma', MainProductFigmaElement)
  }

  class MainProductCountdownElement extends window.HTMLElement {
    #lifecycle = null

    connectedCallback() {
      this.#lifecycle?.dispose()
      this.#lifecycle = createLifecycleRegistry()
      this.#render()
      this.#lifecycle.trackInterval(window.setInterval(() => this.#render(), 1000))
      document.addEventListener('visibilitychange', () => this.#render(), {
        signal: this.#lifecycle.signal,
      })
    }

    disconnectedCallback() {
      this.#lifecycle?.dispose()
      this.#lifecycle = null
    }

    #render() {
      const state = computeCountdownState(this.getAttribute('data-ends-at'), Date.now())
      this.hidden = state.hidden || !state.valid
      this.querySelector('time')?.toggleAttribute('hidden', state.expired)
      this.querySelector('[data-countdown-ended]')?.toggleAttribute('hidden', !state.expired)

      const values = {
        days: state.days,
        hours: state.hours,
        minutes: state.minutes,
        seconds: state.seconds,
      }
      for (const [unit, value] of Object.entries(values)) {
        const output = this.querySelector(`[data-countdown-${unit}]`)
        if (output) output.textContent = String(value).padStart(2, '0')
      }
    }
  }

  if (!window.customElements.get('main-product-countdown')) {
    window.customElements.define('main-product-countdown', MainProductCountdownElement)
  }

  class MainProductShareCopyElement extends window.HTMLElement {
    #copyRequestEpoch = 0
    #lifecycle = null
    #restoreTimer = null

    connectedCallback() {
      this.#copyRequestEpoch += 1
      this.#lifecycle?.dispose()
      this.#lifecycle = createLifecycleRegistry()
      this.addEventListener('click', (event) => this.#onClick(event), {
        signal: this.#lifecycle.signal,
      })
    }

    disconnectedCallback() {
      this.#copyRequestEpoch += 1
      this.#clearStatus()
      this.#lifecycle?.dispose()
      this.#lifecycle = null
      this.#restoreTimer = null
    }

    #clearStatus() {
      if (this.#restoreTimer !== null) window.clearTimeout(this.#restoreTimer)
      this.#restoreTimer = null
      this.removeAttribute('data-copy-state')
      const status = this.querySelector('[data-main-product-figma-copy-status]')
      if (status) status.textContent = ''
    }

    async #onClick(event) {
      const button = event.target.closest?.('[data-main-product-figma-copy-link]')
      if (!button) return
      event.preventDefault?.()

      const lifecycle = this.#lifecycle
      if (!lifecycle) return

      const requestEpoch = ++this.#copyRequestEpoch
      const shareUrl = button.getAttribute('data-share-url') ?? ''
      this.#clearStatus()

      let copied
      try {
        if (shareUrl === '') throw new Error('missing share URL')
        await navigator.clipboard.writeText(shareUrl)
        copied = true
      } catch {
        copied = false
      }

      if (
        this.#lifecycle !== lifecycle ||
        this.#copyRequestEpoch !== requestEpoch ||
        !this.isConnected
      ) {
        return
      }

      const status = this.querySelector('[data-main-product-figma-copy-status]')
      const message = this.getAttribute(copied ? 'data-copied' : 'data-copy-failed') ?? ''
      this.setAttribute('data-copy-state', copied ? 'copied' : 'failed')
      if (status) status.textContent = message

      this.#restoreTimer = lifecycle.trackTimeout(
        window.setTimeout(() => {
          if (this.#lifecycle !== lifecycle || this.#copyRequestEpoch !== requestEpoch) return
          this.#clearStatus()
        }, 3000),
      )
    }
  }

  if (!window.customElements.get('main-product-share-copy')) {
    window.customElements.define('main-product-share-copy', MainProductShareCopyElement)
  }

  class MainProductTierCopyElement extends window.HTMLElement {
    #activeButton = null
    #copyRequestEpoch = 0
    #lifecycle = null
    #restoreTimer = null

    connectedCallback() {
      this.#copyRequestEpoch += 1
      this.#lifecycle?.dispose()
      this.#lifecycle = createLifecycleRegistry()
      this.addEventListener('click', (event) => this.#onClick(event), {
        signal: this.#lifecycle.signal,
      })
    }

    disconnectedCallback() {
      this.#copyRequestEpoch += 1
      this.#restoreActiveButton()
      this.removeAttribute('data-copy-state')
      const status = this.querySelector('[data-tier-status]')
      if (status) status.textContent = ''
      this.#lifecycle?.dispose()
      this.#lifecycle = null
      this.#restoreTimer = null
    }

    #restoreActiveButton() {
      if (!this.#activeButton) return
      const defaultLabel =
        this.#activeButton.getAttribute('data-copy-default') ?? this.#activeButton.textContent
      this.#activeButton.textContent = defaultLabel
      this.#activeButton.removeAttribute('data-copy-active')
      this.#activeButton = null
    }

    async #onClick(event) {
      const button = event.target.closest?.('[data-tier-code]')
      if (!button) return
      const lifecycle = this.#lifecycle
      if (!lifecycle) return
      const requestEpoch = ++this.#copyRequestEpoch
      const status = this.querySelector('[data-tier-status]')
      if (this.#restoreTimer !== null) {
        window.clearTimeout(this.#restoreTimer)
        this.#restoreTimer = null
      }
      this.#restoreActiveButton()
      this.removeAttribute('data-copy-state')
      if (status) status.textContent = ''
      const defaultLabel = button.getAttribute('data-copy-default') ?? button.textContent
      let copied

      try {
        await navigator.clipboard.writeText(button.getAttribute('data-tier-code') ?? '')
        copied = true
      } catch {
        copied = false
      }

      if (
        this.#lifecycle !== lifecycle ||
        this.#copyRequestEpoch !== requestEpoch ||
        !this.isConnected
      ) {
        return
      }

      if (copied) {
        button.textContent = button.getAttribute('data-tier-code') ?? ''
        button.setAttribute('data-copy-active', '')
        this.#activeButton = button
        if (status) status.textContent = this.getAttribute('data-copied') ?? ''
        this.setAttribute('data-copy-state', 'copied')
      } else {
        button.textContent = defaultLabel
        if (status) status.textContent = this.getAttribute('data-copy-failed') ?? ''
        this.setAttribute('data-copy-state', 'failed')
      }

      if (this.#restoreTimer !== null) window.clearTimeout(this.#restoreTimer)
      this.#restoreTimer = lifecycle.trackTimeout(
        window.setTimeout(() => {
          if (this.#lifecycle !== lifecycle || this.#copyRequestEpoch !== requestEpoch) return
          button.textContent = defaultLabel
          button.removeAttribute('data-copy-active')
          if (this.#activeButton === button) this.#activeButton = null
          if (status) status.textContent = ''
          this.removeAttribute('data-copy-state')
          this.#restoreTimer = null
        }, 5000),
      )
    }
  }

  if (!window.customElements.get('main-product-tier-copy')) {
    window.customElements.define('main-product-tier-copy', MainProductTierCopyElement)
  }
}

registerBrowserElement()
